import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Loader2, Sparkles, CheckCircle, Wand2, X, Layers, Pencil, AlertTriangle, Info, Lightbulb, ChevronDown, ChevronUp, Link2, Plus, GraduationCap, Tag, Briefcase, Zap, BookOpen, Languages } from 'lucide-react';
import { getUser, getAuthToken, authenticatedFetch } from '@/lib/auth';
import { useToast } from './ui';
import { validateECTSAlignment, validateLearningOutcomes } from '@/lib/validation';
import type { StudyPlanFormData, ValidationResponse, ValidationFeedback, StudyPlanModule, OutcomeESCOMapping, ESCOOccupationMatch, ESCOSkillMatch } from '@/types';
import LiteratureManager from './LiteratureManager';
import ModuleEditor from './ModuleEditor';
import LearningOutcomeEditor, { filterConfirmedESCOMappings } from './LearningOutcomeEditor';
import OccupationMatcher from './OccupationMatcher';
import TranslationEditor from './TranslationEditor';

export default function StudyPlanForm() {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [approving, setApproving] = useState(false);
  const [showSelfApproveModal, setShowSelfApproveModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [showSubmitForReviewModal, setShowSubmitForReviewModal] = useState(false);
  const [submissionJustification, setSubmissionJustification] = useState('');
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [createdPlanId, setCreatedPlanId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [needsRevalidation, setNeedsRevalidation] = useState(false);
  const [validationRunThisSession, setValidationRunThisSession] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<StudyPlanFormData | null>(null);
  const [studyPlanStatus, setStudyPlanStatus] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showValidationConfirm, setShowValidationConfirm] = useState(false);

  // AI Improvement suggestions
  const [improvements, setImprovements] = useState<any>(null);
  const [loadingImprovements, setLoadingImprovements] = useState(false);
  const [showImprovements, setShowImprovements] = useState(false);

  // AI Generation inputs
  const [generateDescription, setGenerateDescription] = useState('');
  const [generateTitle, setGenerateTitle] = useState('');
  const [generateECTS, setGenerateECTS] = useState<number | ''>('');
  const [generateHours, setGenerateHours] = useState<number | ''>('');
  const [generateEqfLevel, setGenerateEqfLevel] = useState('5');
  const [generateTargetGroup, setGenerateTargetGroup] = useState('');
  const [generateAdmissionReq, setGenerateAdmissionReq] = useState('');

  const [formData, setFormData] = useState<StudyPlanFormData>({
    title: '',
    description: '',
    ects_credits: 0,
    estimated_hours: 0,
    learning_outcomes: {
      knowledge: [],
      skills: [],
      competence: [],
    },
    content_modules: [],
    assessment_criteria: '',
    metadata: {
      language: 'no',
      country: 'NO',
      eqf_level: '5',
      assessment_methods: [],
    },
    // New fields for enhanced validation
    target_group: '',
    admission_requirements: '',
    teaching_learning_methods: '',
  });

  // Temporary input states for arrays
  const [moduleInput, setModuleInput] = useState('');
  const [assessmentMethodInput, setAssessmentMethodInput] = useState('');

  // Editing state for inline editing
  const [editingItem, setEditingItem] = useState<{field: string; index: number; value: string} | null>(null);

  // ESCO Skills state
  const [escoSearchQuery, setEscoSearchQuery] = useState('');
  const [escoSearchResults, setEscoSearchResults] = useState<any[]>([]);
  const [escoSearching, setEscoSearching] = useState(false);
  const [selectedEscoSkills, setSelectedEscoSkills] = useState<any[]>([]);

  // Per-outcome ESCO mappings
  const [outcomeEscoMappings, setOutcomeEscoMappings] = useState<OutcomeESCOMapping[]>([]);

  // Target group and admission requirements occupation mappings
  const [targetGroupOccupations, setTargetGroupOccupations] = useState<ESCOOccupationMatch[]>([]);
  const [admissionOccupations, setAdmissionOccupations] = useState<ESCOOccupationMatch[]>([]);

  // Module state
  const [modules, setModules] = useState<StudyPlanModule[]>([]);
  const [showModuleSection, setShowModuleSection] = useState(false);

  useEffect(() => {
    // Get current user
    const user = getUser();
    setCurrentUser(user);

    if (id) {
      loadStudyPlan(id);
    }
  }, [id]);

  // Track original ESCO data for change detection
  const [originalEscoData, setOriginalEscoData] = useState<{
    outcomeEscoMappings: OutcomeESCOMapping[];
    targetGroupOccupations: ESCOOccupationMatch[];
    admissionOccupations: ESCOOccupationMatch[];
    selectedEscoSkills: any[];
  } | null>(null);

  // Detect changes to form data and ESCO data
  useEffect(() => {
    if (!originalFormData || !studyPlanStatus) return;

    // Check if formData has changed from original
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);

    // Check if ESCO-related data has changed
    let escoDataChanged = false;
    if (originalEscoData) {
      escoDataChanged =
        JSON.stringify(outcomeEscoMappings) !== JSON.stringify(originalEscoData.outcomeEscoMappings) ||
        JSON.stringify(targetGroupOccupations) !== JSON.stringify(originalEscoData.targetGroupOccupations) ||
        JSON.stringify(admissionOccupations) !== JSON.stringify(originalEscoData.admissionOccupations) ||
        JSON.stringify(selectedEscoSkills) !== JSON.stringify(originalEscoData.selectedEscoSkills);
    }

    const hasChanged = formDataChanged || escoDataChanged;

    setHasUnsavedChanges(hasChanged);

    // If plan is approved and data has changed, require revalidation
    if (studyPlanStatus === 'approved' && hasChanged) {
      setNeedsRevalidation(true);
      setValidationResult(null); // Clear old validation result since plan has changed
    }
  }, [formData, originalFormData, studyPlanStatus, outcomeEscoMappings, targetGroupOccupations, admissionOccupations, selectedEscoSkills, originalEscoData]);

  // Auto-hide success message after 5 seconds and scroll to top
  useEffect(() => {
    if (successMessage) {
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadStudyPlan = async (planId: string) => {
    setLoading(true);
    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Call email-service API to get study plan
      const response = await authenticatedFetch('/api/study-plans/get', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: planId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load study plan');
      }

      const data = result.study_plan;

      if (data) {
        const loadedFormData = {
          title: data.title,
          description: data.description,
          ects_credits: data.ects_credits,
          estimated_hours: data.estimated_hours,
          learning_outcomes: data.learning_outcomes,
          content_modules: data.content_modules,
          assessment_criteria: data.assessment_criteria,
          metadata: data.metadata,
          isced_f_code: data.isced_f_code || null,
          esco_skills: data.esco_skills || null,
          mc_metadata: data.mc_metadata || null,
          // New fields for enhanced validation
          target_group: data.target_group || '',
          admission_requirements: data.admission_requirements || '',
          teaching_learning_methods: data.teaching_learning_methods || '',
        };

        setFormData(loadedFormData);
        // Deep copy for originalFormData to avoid shared references with formData
        // Using JSON.parse/stringify to ensure exact same structure
        setOriginalFormData(JSON.parse(JSON.stringify(loadedFormData)));
        setStudyPlanStatus(data.status || '');
        setHasUnsavedChanges(false);

        // If editing an approved plan, show version warning immediately
        // and allow editing of all fields including literature
        // Also clear old validation result - user must re-validate for new version
        console.log('[StudyPlanForm] Loading plan with status:', data.status);
        if (data.status === 'approved') {
          console.log('[StudyPlanForm] Setting needsRevalidation=true for approved plan');
          setNeedsRevalidation(true);
          setValidationResult(null); // Clear old validation - new version needs new validation
        } else {
          setNeedsRevalidation(false);
          // Only load validation result for non-approved plans
          if (data.validation_feedback) {
            // Determine decision from feedback or score
            const decision = data.validation_decision ||
              data.validation_feedback?.decision ||
              (data.validation_score >= 80 ? 'approved' :
               data.validation_score >= 60 ? 'rejected_with_revisions' : 'rejected');
            setValidationResult({
              score: data.validation_score,
              feedback: data.validation_feedback,
              passed: data.validation_score >= 70,
              decision: decision,
            });
          }
        }

        // Initialize ESCO skills if present
        if (data.esco_skills && Array.isArray(data.esco_skills)) {
          setSelectedEscoSkills(data.esco_skills);
        }

        // Initialize per-outcome ESCO mappings from mc_metadata
        if (data.mc_metadata?.outcome_esco_mappings && Array.isArray(data.mc_metadata.outcome_esco_mappings)) {
          setOutcomeEscoMappings(data.mc_metadata.outcome_esco_mappings);
        }

        // Initialize target group and admission occupations from mc_metadata
        const loadedTargetGroupOccupations = data.mc_metadata?.target_group_occupations || [];
        const loadedAdmissionOccupations = data.mc_metadata?.admission_occupations || [];
        const loadedOutcomeEscoMappings = data.mc_metadata?.outcome_esco_mappings || [];

        if (Array.isArray(loadedTargetGroupOccupations)) {
          setTargetGroupOccupations(loadedTargetGroupOccupations);
        }
        if (Array.isArray(loadedAdmissionOccupations)) {
          setAdmissionOccupations(loadedAdmissionOccupations);
        }

        // Save original ESCO data for change detection
        setOriginalEscoData({
          outcomeEscoMappings: loadedOutcomeEscoMappings,
          targetGroupOccupations: loadedTargetGroupOccupations,
          admissionOccupations: loadedAdmissionOccupations,
          selectedEscoSkills: data.esco_skills || []
        });

        // Load modules for this study plan
        try {
          const modulesResponse = await authenticatedFetch(`/api/ai/modules/${planId}`);
          if (modulesResponse.ok) {
            const modulesData = await modulesResponse.json();
            if (modulesData.success && modulesData.modules) {
              setModules(modulesData.modules);
              if (modulesData.modules.length > 0) {
                setShowModuleSection(true);
              }
            }
          }
        } catch (e) {
          console.warn('Could not load modules:', e);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (status: 'draft' | 'submitted' = 'draft') => {
    setSaving(true);
    setError('');

    try {
      // Validate ECTS alignment
      const ectsValidation = validateECTSAlignment(
        formData.ects_credits,
        formData.estimated_hours
      );
      if (!ectsValidation.valid) {
        throw new Error(ectsValidation.message);
      }

      // Validate learning outcomes
      const outcomesValidation = validateLearningOutcomes(formData.learning_outcomes);
      if (!outcomesValidation.valid) {
        throw new Error(outcomesValidation.errors.join(', '));
      }

      // Use custom auth instead of Supabase auth
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      if (!user?.study_association_id) {
        throw new Error('No study association found');
      }

      // Use id from URL or createdPlanId from first save
      const existingPlanId = id || createdPlanId;

      if (existingPlanId) {
        // Update existing via email-service API
        // Always save as draft first - we'll use submit endpoint for actual submission
        const saveStatus = studyPlanStatus === 'approved' ? 'draft' : 'draft';

        const response = await authenticatedFetch('/api/study-plans/update', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: existingPlanId,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            status: saveStatus,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to update study plan');
        }

        // If submitting, call the dedicated submit endpoint after saving
        if (status === 'submitted') {
          const submitResponse = await authenticatedFetch('/api/study-plans/submit', {
            method: 'POST',
            body: JSON.stringify({
              user_id: user.id,
              study_plan_id: existingPlanId,
            }),
          });

          if (!submitResponse.ok) {
            throw new Error(`HTTP error! status: ${submitResponse.status}`);
          }

          const submitResult = await submitResponse.json();
          if (!submitResult.success) {
            throw new Error(submitResult.error || 'Failed to submit study plan');
          }
        }
      } else {
        // Create new via email-service API
        const response = await authenticatedFetch('/api/study-plans/create', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to create study plan');
        }

        // If creating new, save the new ID and stay on page
        // API returns study_plan_id directly, not study_plan.id
        const newPlanId = result.study_plan_id || result.study_plan?.id;
        if (newPlanId) {
          setCreatedPlanId(newPlanId);

          // Update URL without full page reload
          window.history.replaceState(null, '', `/study-plans/${newPlanId}`);

          // Save modules if we have them and now have an ID
          if (modules.length > 0) {
            try {
              await authenticatedFetch(`/api/ai/modules/apply`, {
                method: 'POST',
                body: JSON.stringify({
                  study_plan_id: newPlanId,
                  modules: modules,
                }),
              });
            } catch (e) {
              console.warn('Could not save modules for new plan:', e);
            }
          }

          // Update state for the newly created plan
          // Deep copy to avoid shared references with formData
          setOriginalFormData(JSON.parse(JSON.stringify(formData)));
          setHasUnsavedChanges(false);
          setStudyPlanStatus('draft');
        }
      }

      // Save modules if we have them and are updating an existing plan
      if (id && modules.length > 0) {
        try {
          // Delete existing modules and recreate
          await authenticatedFetch(`/api/ai/modules/${id}`, {
            method: 'DELETE',
          });
          await authenticatedFetch(`/api/ai/modules/apply`, {
            method: 'POST',
            body: JSON.stringify({
              study_plan_id: id,
              modules: modules,
            }),
          });
        } catch (e) {
          console.warn('Could not save modules:', e);
        }
      }

      // Update state after save
      if (id) {
        // Deep copy to avoid shared references with formData
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setHasUnsavedChanges(false);

        // Update status in component state
        if (studyPlanStatus === 'approved') {
          setStudyPlanStatus('draft');
        }
      }

      // Show success message and decide whether to navigate
      if (status === 'submitted') {
        // Submitting - show message then navigate back
        setSuccessMessage('Studieplan sendt til studieforbund for godkjenning!');
        setTimeout(() => {
          navigate('/study-plans');
        }, 1500);
      } else if (id && status === 'draft') {
        // Saving draft on existing plan - stay on page with success message
        if (studyPlanStatus === 'approved') {
          setSuccessMessage('Endringer lagret som nytt utkast! Kjør KI-validering for å godkjenne som ny versjon.');
        } else {
          setSuccessMessage('Studieplan lagret som utkast');
        }
      } else if (!id && status === 'draft') {
        // Creating new plan as draft - stay on page
        setSuccessMessage('Studieplan opprettet! Du kan nå kjøre KI-validering.');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Wrapper function that checks for unsaved changes before validation
  const handleValidateClick = () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Vennligst lagre studieplanen først før du kjører KI-validering.');
      return;
    }

    // If there are unsaved changes, show confirmation box
    if (hasUnsavedChanges) {
      setShowValidationConfirm(true);
      return;
    }

    // No unsaved changes, run validation directly
    handleValidate();
  };

  const handleValidate = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Vennligst lagre studieplanen først før du kjører KI-validering.');
      return;
    }

    // Close confirmation box if open
    setShowValidationConfirm(false);
    setValidating(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // FIRST: If there are unsaved changes, save them to study_plans table
      if (hasUnsavedChanges) {
        const updateResponse = await authenticatedFetch('/api/study-plans/update', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: planId,
            title: formData.title,
            description: formData.description,
            ects_credits: formData.ects_credits,
            estimated_hours: formData.estimated_hours,
            learning_outcomes: formData.learning_outcomes,
            content_modules: formData.content_modules,
            assessment_criteria: formData.assessment_criteria,
            metadata: formData.metadata,
            // New fields for enhanced validation
            target_group: formData.target_group,
            admission_requirements: formData.admission_requirements,
            teaching_learning_methods: formData.teaching_learning_methods,
            isced_f_code: formData.isced_f_code,
            esco_skills: formData.esco_skills,
            mc_metadata: {
              ...formData.mc_metadata,
              // ESCO v2.2: Filter out unconfirmed medium/low tier matches
              outcome_esco_mappings: filterConfirmedESCOMappings(outcomeEscoMappings),
              target_group_occupations: targetGroupOccupations,
              admission_occupations: admissionOccupations,
            },
            status: null, // Don't change status
          }),
        });

        if (!updateResponse.ok) {
          throw new Error(`Failed to save changes: ${updateResponse.status}`);
        }

        const updateResult = await updateResponse.json();
        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to save changes');
        }

        // Update original form data after successful save
        // Deep copy to avoid shared references with formData
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setHasUnsavedChanges(false);
      }

      // SECOND: Validate study plan with automatic English translation
      // The new endpoint generates English translation if missing, then validates both languages
      const response = await authenticatedFetch(`/api/study-plans/${planId}/validate-with-translation`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        // Check if it's a translation quality failure
        if (result.translation_quality && result.translation_quality.score < 60) {
          setError(`Engelsk oversettelse oppfyller ikke kvalitetskrav (score: ${result.translation_quality.score}/100). ${result.suggestion || ''}`);
          // Still show the partial result for debugging
          if (result.norwegian_score) {
            setValidationResult({
              score: result.norwegian_score,
              feedback: { translation_quality: result.translation_quality } as ValidationFeedback,
              passed: result.norwegian_passed,
              decision: 'rejected_with_revisions'
            });
          }
          return;
        }
        throw new Error(result.error || 'Validation failed');
      }

      setValidationResult(result.validation_result);
      setNeedsRevalidation(false); // Validation done, can approve now
      setValidationRunThisSession(true); // Mark that validation was run in this session

      // If translation was generated, update local form data with English fields
      if (result.translation) {
        setFormData(prev => ({
          ...prev,
          title_en: result.translation.title_en,
          description_en: result.translation.description_en,
          target_group_en: result.translation.target_group_en,
          admission_requirements_en: result.translation.admission_requirements_en,
          teaching_learning_methods_en: result.translation.teaching_learning_methods_en,
          assessment_criteria_en: result.translation.assessment_criteria_en,
          learning_outcomes_en: result.translation.learning_outcomes_en,
          content_modules_en: result.translation.content_modules_en,
        }));
      }

      // Don't reload - it would overwrite the form with data from study_plan_versions
      // Just update validation result and keep the current form data

      // Show success message with translation info
      const hasTranslation = result.translation || result.translation_quality;
      const translationScore = result.translation_quality?.score;
      setSuccessMessage(hasTranslation
        ? `KI-validering fullført! Oversettelse kvalitetsscore: ${translationScore}/100`
        : 'KI-validering fullført!');
      // Clear any previous improvements when validation is re-run
      setImprovements(null);
      setShowImprovements(false);
    } catch (err: any) {
      setError('Feil ved validering: ' + err.message);
    } finally {
      setValidating(false);
    }
  };

  // Fetch AI improvement suggestions
  const handleFetchImprovements = async () => {
    const planId = id || createdPlanId;
    if (!planId || !validationResult) {
      return;
    }

    // Check for unsaved changes - must save first
    if (hasUnsavedChanges) {
      setError('Du må lagre endringene før du kan hente KI-forslag. Trykk "Lagre utkast" først.');
      return;
    }

    setLoadingImprovements(true);
    setError('');

    try {
      const response = await authenticatedFetch('/api/study-plans/improve', {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: planId,
          validation_feedback: validationResult.feedback
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate improvements');
      }

      // Include suggested_outcome_skills in improvements object for easy access
      const improvementsWithSkills = {
        ...result.improvements,
        suggested_outcome_skills: result.suggested_outcome_skills || null
      };
      setImprovements(improvementsWithSkills);
      setShowImprovements(true);
    } catch (err: any) {
      setError('Feil ved generering av forbedringsforslag: ' + err.message);
    } finally {
      setLoadingImprovements(false);
    }
  };

  // Apply a single improvement to a field and remove it from suggestions
  const applyImprovement = (field: string, value: any, subField?: string, index?: number) => {
    // Apply the change to formData
    if (field === 'learning_outcomes' && subField && typeof index === 'number') {
      // Deep copy to avoid mutating originalFormData - preserve exact structure
      const outcomes = JSON.parse(JSON.stringify(formData.learning_outcomes || {}));
      const typeKey = subField as 'knowledge' | 'skills' | 'competence';
      if (outcomes[typeKey] && outcomes[typeKey][index] !== undefined) {
        outcomes[typeKey][index] = value;
        setFormData({...formData, learning_outcomes: outcomes});
        setHasUnsavedChanges(true);
        setNeedsRevalidation(true);
      }
    } else if (field === 'content_modules' && typeof index === 'number') {
      const modules = [...(formData.content_modules || [])];
      if (modules[index] !== undefined) {
        modules[index] = value;
        setFormData({...formData, content_modules: modules});
        setHasUnsavedChanges(true);
        setNeedsRevalidation(true);
      }
    } else if (field === 'metadata' && subField) {
      // Handle metadata fields - each has different storage location
      if (subField === 'esco_skills') {
        // ESCO skills are stored in separate state and use a different format
        // Convert string array to ESCO skill objects
        const escoSkillObjects = (value || []).map((skillName: string, idx: number) => ({
          uri: `ai-suggested-${idx}`,
          title: skillName,
          description: 'KI-foreslått ferdighet'
        }));
        setSelectedEscoSkills(escoSkillObjects);
      } else if (subField === 'eqf_level') {
        // EQF level is inside metadata
        setFormData({
          ...formData,
          metadata: { ...formData.metadata, eqf_level: value as any }
        });
      } else if (subField === 'isced_code') {
        // ISCED code is a top-level field (not in metadata)
        setFormData({
          ...formData,
          isced_f_code: value
        });
      } else if (subField === 'keywords') {
        // Keywords - store in metadata.keywords array
        setFormData({
          ...formData,
          metadata: { ...formData.metadata, keywords: value as string[] }
        });
      }
      setHasUnsavedChanges(true);
      setNeedsRevalidation(true);
    } else {
      setFormData({...formData, [field]: value});
      setHasUnsavedChanges(true);
      setNeedsRevalidation(true);
    }

    // Remove the applied improvement from the suggestions list
    if (improvements) {
      const updatedImprovements = {...improvements};

      if (field === 'learning_outcomes' && subField && typeof index === 'number') {
        // Mark specific learning outcome as applied
        if (updatedImprovements.learning_outcomes?.[subField]) {
          updatedImprovements.learning_outcomes[subField] = updatedImprovements.learning_outcomes[subField].map((item: any) =>
            item.index === index ? {...item, needs_improvement: false} : item
          );
        }
      } else if (field === 'content_modules' && typeof index === 'number') {
        // Mark specific content module as applied
        if (updatedImprovements.content_modules) {
          updatedImprovements.content_modules = updatedImprovements.content_modules.map((item: any) =>
            item.index === index ? {...item, needs_improvement: false} : item
          );
        }
      } else if (field === 'metadata' && subField) {
        // Mark metadata field as applied
        if (updatedImprovements.metadata?.[subField]) {
          updatedImprovements.metadata[subField] = {...updatedImprovements.metadata[subField], needs_improvement: false};
        }
      } else {
        // Simple field - mark as no longer needing improvement
        if (updatedImprovements[field]) {
          updatedImprovements[field] = {...updatedImprovements[field], needs_improvement: false};
        }
      }

      setImprovements(updatedImprovements);
    }

    setSuccessMessage('Forslag brukt! Husk å lagre og kjøre ny validering.');
  };

  const handleGenerate = async () => {
    if (!generateDescription.trim()) {
      toast.showWarning('Vennligst beskriv studieplanen du ønsker å generere');
      return;
    }

    // Validate minimum hours (required for generation)
    const hours = generateHours || (generateECTS ? Number(generateECTS) * 25 : 0);
    if (!hours || hours < 4) {
      toast.showWarning('Vennligst angi estimerte timer (minimum 4 timer for Kompetanseattest)');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch('/api/ai/generate', {
        method: 'POST',
        body: JSON.stringify({
          description: generateDescription,
          title: generateTitle || undefined,
          ects_credits: generateECTS || undefined,
          estimated_hours: hours,
          eqf_level: generateEqfLevel || '5',
          target_group: generateTargetGroup || undefined,
          admission_requirements: generateAdmissionReq || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Generering feilet');
      }

      const { generated_plan, esco_suggestions, outcome_esco_mappings, target_group_occupations } = await response.json();

      console.log('AI Generated Plan:', generated_plan);
      console.log('ESCO Suggestions:', esco_suggestions);
      console.log('Outcome ESCO Mappings:', outcome_esco_mappings);
      console.log('Target Group Occupations:', target_group_occupations);

      // Fill form with generated data
      const newFormData: StudyPlanFormData = {
        title: generated_plan.title || '',
        description: generated_plan.description || '',
        ects_credits: generated_plan.ects_credits || 0,
        estimated_hours: generated_plan.estimated_hours || 0,
        learning_outcomes: {
          knowledge: generated_plan.learning_outcomes?.knowledge || [],
          skills: generated_plan.learning_outcomes?.skills || [],
          competence: generated_plan.learning_outcomes?.competence || [],
        },
        content_modules: generated_plan.content_modules?.map((m: any) =>
          typeof m === 'string' ? m : `${m.title}: ${m.description} (${m.hours}t)`
        ) || [],
        assessment_criteria: generated_plan.assessment_criteria || '',
        metadata: {
          language: generated_plan.metadata?.language || 'no',
          country: generated_plan.metadata?.country || 'NO',
          eqf_level: generated_plan.metadata?.eqf_level || generateEqfLevel || '5',
          assessment_methods: generated_plan.metadata?.assessment_methods || [],
        },
        // New fields from enhanced generation
        target_group: generated_plan.target_group || generateTargetGroup || '',
        admission_requirements: generated_plan.admission_requirements || generateAdmissionReq || '',
        teaching_learning_methods: generated_plan.teaching_learning_methods || '',
        // ISCED-F code from AI generation
        isced_f_code: generated_plan.isced_f_code || null,
      };

      // Automatically add ESCO skills if suggestions were returned
      if (esco_suggestions && esco_suggestions.length > 0) {
        newFormData.esco_skills = esco_suggestions.map((skill: any) => ({
          uri: skill.uri,
          code: skill.code,
          title: skill.title,
          description: skill.description || '',
          skillType: skill.skillType || 'skill/competence',
          reuseLevel: skill.reuseLevel || 'cross-sectoral',
          isced_f: skill.isced_f || null,
        }));
        console.log('Added', esco_suggestions.length, 'ESCO skills automatically');
      }

      console.log('New Form Data:', newFormData);
      setFormData(newFormData);

      // Set ESCO mappings for learning outcomes (from NB-SBERT matching)
      if (outcome_esco_mappings && Object.keys(outcome_esco_mappings).length > 0) {
        const mappings: OutcomeESCOMapping[] = [];
        for (const [key, skills] of Object.entries(outcome_esco_mappings)) {
          const [type, indexStr] = key.split('-');
          mappings.push({
            outcomeIndex: parseInt(indexStr),
            outcomeType: type as 'knowledge' | 'skills' | 'competence',
            escoSkills: skills as ESCOSkillMatch[]
          });
        }
        setOutcomeEscoMappings(mappings);
        console.log('Set', mappings.length, 'ESCO outcome mappings');
      }

      // Set ESCO occupations for target group
      if (target_group_occupations && target_group_occupations.length > 0) {
        setTargetGroupOccupations(target_group_occupations);
        console.log('Set', target_group_occupations.length, 'target group occupations');
      }

      setShowGenerateModal(false);
      setGenerateDescription('');
      setGenerateTitle('');
      setGenerateECTS('');
      setHasUnsavedChanges(true);

      setSuccessMessage('Studieplan generert med ESCO-koblinger! Se gjennom og juster etter behov.');
    } catch (err: any) {
      setError('Feil ved generering: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      setError('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult) {
      setError('Du må kjøre KI-validering før godkjenning');
      return;
    }

    // This function is only called when score >= 70, so no need to check for low score
    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Build complete comment with KI-validation result + admin notes
      const fullComment = `KI-VALIDERING:
Score: ${Number(validationResult?.score || 0).toFixed(1)}%
Tilbakemelding: ${validationResult?.feedback?.summary || 'Ingen tilbakemelding'}

${approveComment.trim() ? `MERKNADER:\n${approveComment.trim()}` : ''}`;

      // Approve (which creates version snapshot from study_plans)
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id,
          study_plan_id: planId,
          change_comment: fullComment,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to approve study plan');
      }

      setSuccessMessage('Studieplan godkjent! Godkjenningslenke er generert.');
      setShowApproveModal(false);
      setApproveComment('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved godkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleSelfApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      toast.showWarning('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      toast.showWarning('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult || validationResult.score < 70) {
      toast.showWarning('Studieplanen må ha en KI-score på minst 70% for å kunne selvgodkjennes');
      return;
    }

    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Self-approve via API (member approves their own plan)
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id, // Member approves their own plan
          study_plan_id: planId,
          change_comment: `Selvgodkjent basert på kvalitetssikringssystemets valideringsresultat (Resultat ${Number(validationResult.score).toFixed(1)} %)`,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to self-approve study plan');
      }

      toast.showSuccess('Studieplan selvgodkjent! Godkjenningslenke er generert.');
      setShowSelfApproveModal(false);

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError(err.message);
      toast.showError('Feil ved selvgodkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleOverrideApprove = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke godkjenne en studieplan som ikke er lagret ennå');
      return;
    }

    if (needsRevalidation) {
      setError('Du har gjort endringer som krever ny KI-validering. Vennligst kjør KI-validering før godkjenning.');
      return;
    }

    if (!validationResult) {
      setError('Du må kjøre KI-validering før godkjenning');
      return;
    }

    if (overrideReason.trim().length < 30) {
      setError('Begrunnelsen må være minst 30 tegn');
      return;
    }

    setApproving(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Build override comment with full context
      const fullComment = `KI-VALIDERING OVERPRØVET:
Score: ${Number(validationResult?.score || 0).toFixed(1)}%
KI-tilbakemelding: ${validationResult?.feedback?.summary || 'Ingen tilbakemelding'}

BEGRUNNELSE FOR OVERPRØVING:
${overrideReason.trim()}`;

      // Approve with override flag
      const response = await authenticatedFetch('/api/study-plans/approve', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id,
          study_plan_id: planId,
          change_comment: fullComment,
          override_reason: overrideReason.trim(),
          is_ai_override: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to override approve study plan');
      }

      setSuccessMessage('Studieplan godkjent med KI-overprøving! Godkjenningslenke er generert.');
      setShowOverrideModal(false);
      setOverrideReason('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved godkjenning: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  // Handle submit for review with justification (for rejected_with_revisions plans)
  const handleSubmitForReview = async () => {
    const planId = id || createdPlanId;
    if (!planId) {
      setError('Kan ikke sende inn en studieplan som ikke er lagret ennå');
      return;
    }

    if (submissionJustification.trim().length < 50) {
      setError('Begrunnelsen må være minst 50 tegn');
      return;
    }

    setSubmittingForReview(true);
    setError('');

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/study-plans/submit-for-review', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: planId,
          justification: submissionJustification.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit for review');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit for review');
      }

      setSuccessMessage('Studieplan sendt inn for manuell vurdering!');
      setShowSubmitForReviewModal(false);
      setSubmissionJustification('');

      // Navigate back to study plans list
      navigate('/study-plans');
    } catch (err: any) {
      setError('Feil ved innsending: ' + err.message);
    } finally {
      setSubmittingForReview(false);
    }
  };

  const addModule = () => {
    if (!moduleInput.trim()) return;
    setFormData({
      ...formData,
      content_modules: [...formData.content_modules, moduleInput.trim()],
    });
    setModuleInput('');
  };

  const removeModule = (index: number) => {
    setFormData({
      ...formData,
      content_modules: formData.content_modules.filter((_, i) => i !== index),
    });
  };

  const updateModule = (index: number, value: string) => {
    if (!value.trim()) return;
    const newModules = [...formData.content_modules];
    newModules[index] = value.trim();
    setFormData({
      ...formData,
      content_modules: newModules,
    });
  };

  const addAssessmentMethod = () => {
    if (!assessmentMethodInput.trim()) return;
    setFormData({
      ...formData,
      metadata: {
        ...formData.metadata,
        assessment_methods: [...(formData.metadata.assessment_methods || []), assessmentMethodInput.trim()],
      },
    });
    setAssessmentMethodInput('');
  };

  const updateAssessmentMethod = (index: number, value: string) => {
    if (!value.trim()) return;
    const newMethods = [...(formData.metadata.assessment_methods || [])];
    newMethods[index] = value.trim();
    setFormData({
      ...formData,
      metadata: {
        ...formData.metadata,
        assessment_methods: newMethods,
      },
    });
  };

  // ESCO Skills functions
  const searchEscoSkills = async (query: string) => {
    if (!query.trim()) {
      setEscoSearchResults([]);
      return;
    }

    setEscoSearching(true);
    try {
      const response = await authenticatedFetch(
        `/api/esco/skills/search?q=${encodeURIComponent(query)}&language=en&limit=10`
      );
      const data = await response.json();

      if (data.success) {
        // Normalize results to ensure all fields are strings
        const normalizedResults = (data.results || []).map((skill: any) => {
          const toString = (val: any): string => {
            if (typeof val === 'string') return val;
            if (typeof val === 'object' && val !== null) {
              return val.en || val.no || Object.values(val)[0] || '';
            }
            return String(val || '');
          };
          return {
            uri: toString(skill.uri),
            code: toString(skill.code),
            title: toString(skill.title) || toString(skill.preferredLabel) || 'Ukjent',
            description: toString(skill.description) || '',
            skillType: toString(skill.skillType) || 'skill',
          };
        });
        setEscoSearchResults(normalizedResults);
      } else {
        setError('ESCO søk feilet: ' + data.error);
      }
    } catch (err: any) {
      setError('ESCO søk feilet: ' + err.message);
    } finally {
      setEscoSearching(false);
    }
  };

  const addEscoSkill = (skill: any) => {
    // Check if already selected
    if (selectedEscoSkills.some(s => s.uri === skill.uri)) {
      return;
    }
    setSelectedEscoSkills([...selectedEscoSkills, skill]);
    setEscoSearchQuery('');
    setEscoSearchResults([]);
  };

  const removeEscoSkill = (uri: string) => {
    setSelectedEscoSkills(selectedEscoSkills.filter(s => s.uri !== uri));
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (escoSearchQuery) {
        searchEscoSkills(escoSearchQuery);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [escoSearchQuery]);

  // Save selected ESCO skills to formData
  // VIKTIG: Send alltid array (selv om tom) for å tillate sletting
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      esco_skills: selectedEscoSkills  // Alltid array, tillater tom array for sletting
    }));
  }, [selectedEscoSkills]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{id ? 'Rediger' : 'Opprett'} studieplan</h1>
          {!id && (
            <button
              onClick={() => setShowGenerateModal(true)}
              className="btn btn-secondary flex items-center gap-2 bg-kf-accent hover:bg-kf-accent text-white"
            >
              <Wand2 size={18} />
              KI-generer studieplan
            </button>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Warning when revalidation needed - Version info box */}
      {/* Debug: needsRevalidation={needsRevalidation}, studyPlanStatus={studyPlanStatus} */}
      {needsRevalidation && studyPlanStatus === 'approved' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Layers className="text-blue-600" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 mb-1">
                Du redigerer en godkjent studieplan
              </h4>
              <p className="text-sm text-blue-800 mb-3">
                Endringene dine vil opprette en <strong>ny versjon</strong> av studieplanen når den godkjennes på nytt.
                Den eksisterende versjonen forblir tilgjengelig via sin verifiseringslenke.
              </p>
              <div className="bg-white border border-blue-200 rounded p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-gray-700">Tidligere versjoner bevares og er fortsatt gyldige</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-gray-700">Hver versjon har sin egen verifiseringslenke</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Ny KI-validering kreves før godkjenning</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning for non-approved plans needing revalidation */}
      {needsRevalidation && studyPlanStatus !== 'approved' && (
        <div className="bg-kf-accent-20 border border-kf-accent-40 text-kf-accent px-4 py-3 rounded mb-6">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium">Endringer krever ny KI-validering</p>
              <p className="text-sm mt-1">
                Du har gjort endringer på denne studieplanen. Kjør KI-validering for å se oppdatert kvalitetspoeng.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="text-kf-accent" size={24} />
                  <h2 className="text-2xl font-bold">KI-generer studieplan</h2>
                </div>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Beskriv studieplanen du ønsker, så genererer KI et komplett utkast basert på
                systemadmins regler for kvalitetssikring.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="label">Beskrivelse av studieplan (påkrevd) *</label>
                  <textarea
                    className="input"
                    rows={6}
                    value={generateDescription}
                    onChange={(e) => setGenerateDescription(e.target.value)}
                    placeholder="Eksempel: En studieplan om digital markedsføring som dekker sosiale medier, SEO, og innholdsmarkedsføring. Målgruppen er voksne uten forkunnskaper."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Jo mer detaljert, jo bedre blir resultatet.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Foretrukket tittel (valgfri)</label>
                    <input
                      type="text"
                      className="input"
                      value={generateTitle}
                      onChange={(e) => setGenerateTitle(e.target.value)}
                      placeholder="Digital Markedsføring"
                    />
                  </div>

                  <div>
                    <label className="label">Estimerte timer *</label>
                    <input
                      type="number"
                      className="input"
                      min="4"
                      value={generateHours}
                      onChange={(e) => setGenerateHours(e.target.value === '' ? '' : parseInt(e.target.value))}
                      placeholder="Minimum 4 timer"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Påvirker omfang og kompleksitet
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">EQF-nivå</label>
                    <select
                      className="input"
                      value={generateEqfLevel}
                      onChange={(e) => setGenerateEqfLevel(e.target.value)}
                    >
                      <option value="3">Nivå 3 - Grunnleggende</option>
                      <option value="4">Nivå 4 - Videregående</option>
                      <option value="5">Nivå 5 - Fagskole</option>
                      <option value="6">Nivå 6 - Bachelor</option>
                      <option value="7">Nivå 7 - Master</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Kompetansepoeng (valgfri)</label>
                    <input
                      type="number"
                      className="input"
                      min="0"
                      step="0.5"
                      value={generateECTS}
                      onChange={(e) => setGenerateECTS(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      placeholder="Beregnes automatisk"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Målgruppe (valgfri)</label>
                  <input
                    type="text"
                    className="input"
                    value={generateTargetGroup}
                    onChange={(e) => setGenerateTargetGroup(e.target.value)}
                    placeholder="F.eks. 'Ansatte i helsesektoren'"
                  />
                </div>

                <div>
                  <label className="label">Opptakskrav (valgfri)</label>
                  <input
                    type="text"
                    className="input"
                    value={generateAdmissionReq}
                    onChange={(e) => setGenerateAdmissionReq(e.target.value)}
                    placeholder="F.eks. 'Ingen formelle krav' eller 'Grunnleggende datakunnskaper'"
                  />
                </div>

                <div className="bg-kf-light border border-kf-primary-40 rounded p-4">
                  <p className="text-sm text-kf-primary">
                    <strong>Tips:</strong> KI vil generere læringsutbytte, innholdsmoduler,
                    vurderingskriterier og metadata basert på din beskrivelse. Du kan
                    redigere alt etterpå.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !generateDescription.trim()}
                    className="btn btn-primary flex items-center gap-2 flex-1"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Genererer...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Generer studieplan
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="btn btn-secondary"
                    disabled={generating}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {validationResult && validationResult.score !== undefined && (
        <div className={`border rounded-lg p-6 mb-6 ${
          validationResult.decision === 'approved' ? 'bg-green-50 border-green-200' :
          validationResult.decision === 'rejected_with_revisions' ? 'bg-yellow-50 border-yellow-300' :
          'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Valideringsresultat</h3>
            <div className="flex items-center gap-2">
              {validationResult.decision === 'approved' && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <CheckCircle size={16} /> Godkjent
                </span>
              )}
              {validationResult.decision === 'rejected_with_revisions' && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <AlertTriangle size={16} /> Krever revisjon
                </span>
              )}
              {validationResult.decision === 'rejected' && (
                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center gap-1">
                  <X size={16} /> Avvist
                </span>
              )}
            </div>
          </div>

          {/* Score and Summary */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`text-3xl font-bold ${
              validationResult.score >= 80 ? 'text-green-600' :
              validationResult.score >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {Number(validationResult.score).toFixed(0)}%
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700">{validationResult.feedback?.summary || 'Ingen tilbakemelding'}</p>
            </div>
          </div>

          {/* Scope Classification */}
          {validationResult.feedback?.scope_classification && (
            <div className="bg-white bg-opacity-50 rounded p-3 mb-4">
              <p className="text-sm">
                <span className="font-medium">Omfang:</span> {' '}
                {validationResult.feedback.scope_classification.category === 'micro' && 'Micro (4-15 timer)'}
                {validationResult.feedback.scope_classification.category === 'small' && 'Small (16-49 timer)'}
                {validationResult.feedback.scope_classification.category === 'medium' && 'Medium (50-149 timer)'}
                {validationResult.feedback.scope_classification.category === 'extensive' && 'Extensive (150+ timer)'}
                {' - '}{validationResult.feedback.scope_classification.hours} timer
              </p>
            </div>
          )}

          {/* Scoring Categories Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {validationResult.feedback?.learning_outcomes_quality && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Læringsutbytter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.learning_outcomes_quality.score >= 20 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.learning_outcomes_quality.score}/25
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.learning_outcomes_quality.score / 25) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.constructive_alignment && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Konstruktiv alignment</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.constructive_alignment.score >= 12 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.constructive_alignment.score}/15
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.constructive_alignment.score / 15) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.workload_realism && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Arbeidsbelastning</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.workload_realism.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.workload_realism.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.workload_realism.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.structure_quality && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Struktur</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.structure_quality.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.structure_quality.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.structure_quality.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.microcredential_fitness && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Microcredential</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.microcredential_fitness.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.microcredential_fitness.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.microcredential_fitness.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.metadata_elm_compliance && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Metadata/ELM</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.metadata_elm_compliance.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.metadata_elm_compliance.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.metadata_elm_compliance.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.practical_skills && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Praktiske ferdigheter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.practical_skills.score >= 8 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.practical_skills.score}/10
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.practical_skills.score / 10) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.soft_skills && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Myke ferdigheter</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.soft_skills.score >= 4 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.soft_skills.score}/5
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.soft_skills.score / 5) * 100}%`}}></div>
                </div>
              </div>
            )}
            {validationResult.feedback?.narrative_readiness && (
              <div className="bg-white shadow-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-600">Attesttekst-klar</span>
                  <span className={`text-sm font-bold ${validationResult.feedback.narrative_readiness.score >= 4 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {validationResult.feedback.narrative_readiness.score}/5
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-kf-primary h-1.5 rounded-full" style={{width: `${(validationResult.feedback.narrative_readiness.score / 5) * 100}%`}}></div>
                </div>
              </div>
            )}
          </div>

          {/* NKR/EQF Qualification Framework Alignment */}
          {validationResult.feedback?.qualification_framework_alignment && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-800">
                <GraduationCap size={16} className="text-purple-600" />
                NKR/EQF Nivåvurdering
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Angitt nivå:</span>
                  <span className="font-medium text-purple-800">
                    EQF/NKR {validationResult.feedback.qualification_framework_alignment.claimed_level}
                  </span>
                </div>

                {!validationResult.feedback.qualification_framework_alignment.level_appropriate && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">
                      <strong>Anbefalt nivå:</strong> EQF/NKR {validationResult.feedback.qualification_framework_alignment.recommended_level}
                    </p>
                  </div>
                )}

                {validationResult.feedback.qualification_framework_alignment.level_appropriate && (
                  <div className="bg-white border border-purple-200 rounded-md p-3">
                    <p className="text-purple-700 text-sm flex items-center gap-2">
                      <CheckCircle size={14} />
                      Nivåplassering er passende
                    </p>
                  </div>
                )}

                <p className="text-sm text-gray-600">
                  {validationResult.feedback.qualification_framework_alignment.feedback}
                </p>

                {/* Detailed alignment per category */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {validationResult.feedback.qualification_framework_alignment.knowledge_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.knowledge_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Kunnskap</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.knowledge_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                  {validationResult.feedback.qualification_framework_alignment.skills_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.skills_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Ferdigheter</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.skills_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                  {validationResult.feedback.qualification_framework_alignment.competence_alignment && (
                    <div className={`p-2 rounded text-xs border ${validationResult.feedback.qualification_framework_alignment.competence_alignment.appropriate ? 'bg-white border-purple-200 text-purple-700' : 'bg-yellow-100 border-yellow-200 text-yellow-800'}`}>
                      <div className="font-medium">Kompetanse</div>
                      <div className="truncate">{validationResult.feedback.qualification_framework_alignment.competence_alignment.appropriate ? 'Passende' : 'Avvik'}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ESCO Skills Relevance */}
          {validationResult.feedback?.esco_relevance && (
            <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-teal-800">
                <Tag size={16} className="text-teal-600" />
                ESCO-ferdigheter
              </h4>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-sm text-gray-700">
                  {validationResult.feedback.esco_relevance.relevant_skills} av {validationResult.feedback.esco_relevance.total_skills} ESCO-koblinger er relevante
                </span>
                <span className={`text-sm font-medium ${validationResult.feedback.esco_relevance.score >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                  {validationResult.feedback.esco_relevance.score}%
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {validationResult.feedback.esco_relevance.feedback}
              </p>
            </div>
          )}

          {/* Translation Quality - Oversettelseskvalitet */}
          {validationResult.feedback?.translation_quality && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-blue-800">
                <Languages size={16} className="text-blue-600" />
                Oversettelseskvalitet (Engelsk)
              </h4>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-sm text-gray-700">Kvalitetsscore:</span>
                <span className={`text-xl font-bold ${
                  validationResult.feedback.translation_quality.score >= 80
                    ? 'text-green-600'
                    : validationResult.feedback.translation_quality.score >= 60
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}>
                  {validationResult.feedback.translation_quality.score}%
                </span>
              </div>

              {validationResult.feedback.translation_quality.issues && validationResult.feedback.translation_quality.issues.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Problemer funnet:</p>
                  <ul className="list-disc pl-5 text-sm text-red-600 space-y-1">
                    {validationResult.feedback.translation_quality.issues.map((issue: string, i: number) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-sm text-gray-600">
                {validationResult.feedback.translation_quality.feedback}
              </p>

              {validationResult.feedback.translation_quality.recommendations && validationResult.feedback.translation_quality.recommendations.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm font-medium text-blue-700 mb-1">Anbefalinger:</p>
                  <ul className="list-disc pl-5 text-sm text-blue-600 space-y-1">
                    {validationResult.feedback.translation_quality.recommendations.map((rec: string, i: number) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Bloom Profile Analysis - Holistisk vurdering */}
          {validationResult.feedback?.bloom_profile_analysis && (
            <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-indigo-800">
                <BookOpen size={16} className="text-indigo-600" />
                Bloom-profilanalyse
              </h4>

              <p className="text-sm text-gray-700 mb-3">
                {validationResult.feedback.bloom_profile_analysis.summary}
              </p>

              {/* Kategori-profiler */}
              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                {validationResult.feedback.bloom_profile_analysis.per_category_profile &&
                  Object.entries(validationResult.feedback.bloom_profile_analysis.per_category_profile).map(([cat, data]: [string, any]) => (
                  <div key={cat} className={`p-2 rounded ${
                    data.assessment === 'appropriate' || data.assessment === 'strong'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <span className="font-medium block">
                      {cat === 'knowledge' ? 'Kunnskap' : cat === 'skills' ? 'Ferdigheter' : 'Kompetanse'}
                    </span>
                    <div className="text-gray-600 mt-1">
                      Bloom {data.lowest_level}-{data.highest_level}
                    </div>
                    <div className="text-gray-500 mt-1 text-[10px]">{data.note}</div>
                  </div>
                ))}
              </div>

              {/* Profilbalanse */}
              {validationResult.feedback.bloom_profile_analysis.profile_balance && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_foundation
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_foundation
                      ? '✓ Fundament (Bloom 2-3)' : '⚠ Mangler fundament'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_complexity
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_complexity
                      ? '✓ Kompleksitet (Bloom 4-5)' : '⚠ Mangler kompleksitet'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    validationResult.feedback.bloom_profile_analysis.profile_balance.has_progression
                      ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {validationResult.feedback.bloom_profile_analysis.profile_balance.has_progression
                      ? '✓ Progresjon' : '⚠ Mangler progresjon'}
                  </span>
                </div>
              )}

              {/* Anbefalinger */}
              {validationResult.feedback.bloom_profile_analysis.recommendations &&
               validationResult.feedback.bloom_profile_analysis.recommendations.length > 0 && (
                <div className="mt-2 p-2 bg-amber-100 rounded text-sm">
                  <strong className="text-amber-800">Anbefalinger:</strong>
                  <ul className="list-disc ml-4 mt-1 text-amber-900">
                    {validationResult.feedback.bloom_profile_analysis.recommendations.map((rec: any, i: number) => (
                      <li key={i}>{rec.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-[10px] text-gray-500 mt-2 italic">
                {validationResult.feedback.bloom_profile_analysis.bloom_as_support_tool_note}
              </p>
            </div>
          )}

          {/* Flagged Outcomes with Suggestions */}
          {validationResult.feedback?.flagged_outcomes && validationResult.feedback.flagged_outcomes.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <AlertTriangle size={16} className="text-yellow-600" />
                Læringsutbytter som kan forbedres
              </h4>
              <div className="space-y-3">
                {validationResult.feedback.flagged_outcomes.map((flagged: any, idx: number) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                        {flagged.type === 'knowledge' ? 'Kunnskap' : flagged.type === 'skills' ? 'Ferdighet' : 'Kompetanse'} #{flagged.index + 1}
                      </span>
                      <span className="text-xs text-red-600">{flagged.issue}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-red-50 rounded border border-red-100">
                        <p className="text-xs font-medium text-red-700 mb-1">Nåværende:</p>
                        <p className="text-sm text-gray-700">{flagged.original}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded border border-green-100">
                        <p className="text-xs font-medium text-green-700 mb-1">Forslag:</p>
                        <p className="text-sm text-gray-700">{flagged.suggested}</p>
                        <button
                          type="button"
                          onClick={() => {
                            // Deep copy to avoid mutating originalFormData - preserve exact structure
                            const outcomes = JSON.parse(JSON.stringify(formData.learning_outcomes || {}));
                            const typeKey = flagged.type as 'knowledge' | 'skills' | 'competence';
                            if (outcomes[typeKey] && outcomes[typeKey][flagged.index]) {
                              outcomes[typeKey][flagged.index] = flagged.suggested;
                              setFormData({...formData, learning_outcomes: outcomes});
                              setHasUnsavedChanges(true);
                              setNeedsRevalidation(true);
                            }
                          }}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improvement Suggestions */}
          {validationResult.feedback?.improvement_suggestions && validationResult.feedback.improvement_suggestions.length > 0 && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-blue-800">
                <Info size={16} className="text-blue-600" />
                Forbedringsforslag
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {validationResult.feedback.improvement_suggestions.map((suggestion: string, idx: number) => (
                  <li key={idx} className="text-sm text-blue-900">{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* KI-forslag til justeringer - detailed per-field improvements */}
          <div className="mt-4 border-t pt-4">
            <button
              type="button"
              onClick={handleFetchImprovements}
              disabled={loadingImprovements || hasUnsavedChanges}
              className={`btn flex items-center gap-2 ${
                hasUnsavedChanges
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
              title={hasUnsavedChanges ? 'Lagre endringene først' : ''}
            >
              {loadingImprovements ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Henter KI-forslag...
                </>
              ) : (
                <>
                  <Lightbulb size={18} />
                  {hasUnsavedChanges ? 'Lagre først for KI-forslag' : 'Vis KI-forslag til justeringer'}
                </>
              )}
            </button>

            {/* Collapsible improvements section */}
            {improvements && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowImprovements(!showImprovements)}
                  className="flex items-center gap-2 text-amber-700 hover:text-amber-900 font-medium"
                >
                  {showImprovements ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  {showImprovements ? 'Skjul forslag' : 'Vis forslag'}
                </button>

                {showImprovements && (
                  <div className="mt-3 space-y-4">
                    {/* Summary */}
                    {improvements.summary && (
                      <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                        {improvements.summary}
                      </div>
                    )}

                    {/* Profile Assessment - Expected effect of accepting suggestions */}
                    {improvements.profile_assessment && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <strong className="text-green-800">Forventet effekt av forslag:</strong>
                        <p className="mt-1 text-gray-700">{improvements.profile_assessment.explanation}</p>
                        <div className="flex gap-4 mt-2 text-xs">
                          <span className="text-gray-600">
                            Nå: <span className="font-medium px-1.5 py-0.5 bg-amber-100 rounded">
                              {improvements.profile_assessment.before === 'balanced' ? 'Balansert' :
                               improvements.profile_assessment.before === 'needs_foundation' ? 'Mangler fundament' :
                               improvements.profile_assessment.before === 'needs_complexity' ? 'Mangler kompleksitet' :
                               improvements.profile_assessment.before === 'needs_adjustment' ? 'Behov for justering' :
                               improvements.profile_assessment.before}
                            </span>
                          </span>
                          <span>→</span>
                          <span className="text-gray-600">
                            Etter: <span className="font-medium text-green-700 px-1.5 py-0.5 bg-green-100 rounded">
                              {improvements.profile_assessment.after_if_accepted === 'balanced' ? 'Balansert' :
                               improvements.profile_assessment.after_if_accepted === 'needs_foundation' ? 'Mangler fundament' :
                               improvements.profile_assessment.after_if_accepted === 'needs_complexity' ? 'Mangler kompleksitet' :
                               improvements.profile_assessment.after_if_accepted === 'needs_adjustment' ? 'Behov for justering' :
                               improvements.profile_assessment.after_if_accepted}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* ESCO Skill Suggestions for Learning Outcomes */}
                    {improvements.suggested_outcome_skills && Object.keys(improvements.suggested_outcome_skills).length > 0 && (
                      <div className="p-3 bg-white border border-purple-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                          <Zap size={14} className="text-purple-600" />
                          Foreslåtte ESCO-ferdigheter
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">
                          For læringsutbytter uten ESCO-kobling. Klikk for å legge til.
                        </p>
                        <div className="space-y-3">
                          {Object.entries(improvements.suggested_outcome_skills).map(([key, suggestion]: [string, any]) => {
                            const categoryLabels: Record<string, string> = {
                              knowledge: 'Kunnskap',
                              skills: 'Ferdigheter',
                              competence: 'Kompetanse'
                            };
                            return (
                              <div key={key} className="p-2 bg-purple-50 rounded border border-purple-100">
                                <div className="text-xs text-purple-700 mb-1">
                                  {categoryLabels[suggestion.outcomeType] || suggestion.outcomeType} #{suggestion.outcomeIndex + 1}:
                                </div>
                                <div className="text-sm text-gray-700 mb-2 italic">
                                  "{suggestion.outcomeText}"
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {suggestion.suggestedSkills.map((skill: any) => {
                                    // Check if already added to outcomeEscoMappings
                                    const existingMapping = outcomeEscoMappings.find(
                                      (m: OutcomeESCOMapping) => m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                    );
                                    const isAlreadyAdded = existingMapping?.escoSkills?.some((s: ESCOSkillMatch) => s.uri === skill.uri);

                                    return (
                                      <button
                                        key={skill.uri}
                                        type="button"
                                        disabled={isAlreadyAdded}
                                        onClick={() => {
                                          if (!isAlreadyAdded) {
                                            // Add skill to the mapping for this outcome
                                            const newSkill: ESCOSkillMatch = {
                                              uri: skill.uri,
                                              code: skill.code,
                                              title: skill.title,
                                              description: skill.description,
                                              skillType: skill.skillType || 'skill',
                                              score: skill.score,
                                              confirmed: false
                                            };

                                            setOutcomeEscoMappings((prev: OutcomeESCOMapping[]) => {
                                              const existing = prev.find(
                                                (m: OutcomeESCOMapping) => m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                              );
                                              if (existing) {
                                                // Add to existing mapping
                                                return prev.map((m: OutcomeESCOMapping) =>
                                                  m.outcomeType === suggestion.outcomeType && m.outcomeIndex === suggestion.outcomeIndex
                                                    ? { ...m, escoSkills: [...m.escoSkills, newSkill] }
                                                    : m
                                                );
                                              } else {
                                                // Create new mapping
                                                return [...prev, {
                                                  outcomeIndex: suggestion.outcomeIndex,
                                                  outcomeType: suggestion.outcomeType,
                                                  escoSkills: [newSkill]
                                                }];
                                              }
                                            });
                                            setHasUnsavedChanges(true);
                                            setNeedsRevalidation(true);
                                          }
                                        }}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                          isAlreadyAdded
                                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                            : 'bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200 cursor-pointer'
                                        }`}
                                        title={skill.description || (isAlreadyAdded ? 'Allerede lagt til' : 'Klikk for å legge til')}
                                      >
                                        <Zap size={10} />
                                        <span className="max-w-48 truncate">{skill.title}</span>
                                        <span className={isAlreadyAdded ? 'text-gray-400' : 'text-purple-500'}>
                                          {Math.round((skill.score || 0.5) * 100)}%
                                        </span>
                                        {!isAlreadyAdded && <Plus size={10} />}
                                        {isAlreadyAdded && <CheckCircle size={10} />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Target Group */}
                    {improvements.target_group?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Målgruppe</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700">{improvements.target_group.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700">{improvements.target_group.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.target_group.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('target_group', improvements.target_group.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Occupation suggestions for target group */}
                    {improvements.target_group?.suggested_occupations?.length > 0 && (
                      <div className="p-3 bg-white border border-indigo-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-1.5">
                          <Briefcase size={14} className="text-indigo-600" />
                          Foreslåtte yrkesroller
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">
                          Basert på studieplanens innhold. Klikk for å legge til.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {improvements.target_group.suggested_occupations.map((occ: { uri: string; title: string; description?: string; score?: number }) => {
                            const isAlreadyAdded = targetGroupOccupations.some(o => o.uri === occ.uri);
                            return (
                              <button
                                key={occ.uri}
                                type="button"
                                disabled={isAlreadyAdded}
                                onClick={() => {
                                  if (!isAlreadyAdded) {
                                    setTargetGroupOccupations(prev => [...prev, occ as ESCOOccupationMatch]);
                                    setHasUnsavedChanges(true);
                                    setNeedsRevalidation(true);
                                  }
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                  isAlreadyAdded
                                    ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                    : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 cursor-pointer'
                                }`}
                                title={occ.description || (isAlreadyAdded ? 'Allerede lagt til' : 'Klikk for å legge til')}
                              >
                                <Briefcase size={10} />
                                <span className="max-w-40 truncate">{occ.title}</span>
                                <span className={isAlreadyAdded ? 'text-gray-400' : 'text-indigo-400'}>
                                  {Math.round((occ.score || 0.5) * 100)}%
                                </span>
                                {!isAlreadyAdded && <Plus size={10} />}
                                {isAlreadyAdded && <CheckCircle size={10} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Prerequisites */}
                    {improvements.admission_requirements?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Opptakskrav</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700">{improvements.admission_requirements.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700">{improvements.admission_requirements.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.admission_requirements.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('admission_requirements', improvements.admission_requirements.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Teaching and Learning Methods */}
                    {improvements.teaching_learning_methods?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Undervisnings- og læringsformer</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.teaching_learning_methods.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.teaching_learning_methods.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.teaching_learning_methods.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('teaching_learning_methods', improvements.teaching_learning_methods.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Description */}
                    {improvements.description?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Beskrivelse</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.description.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.description.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.description.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('description', improvements.description.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Assessment Criteria */}
                    {improvements.assessment_criteria?.needs_improvement && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Vurderingskriterier</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Nåværende:</span>
                            <p className="text-gray-700 whitespace-pre-wrap">{improvements.assessment_criteria.current || '(tomt)'}</p>
                          </div>
                          <div>
                            <span className="text-green-600 text-xs">Forslag:</span>
                            <p className="text-green-700 whitespace-pre-wrap">{improvements.assessment_criteria.suggested}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{improvements.assessment_criteria.reason}</p>
                        <button
                          type="button"
                          onClick={() => applyImprovement('assessment_criteria', improvements.assessment_criteria.suggested)}
                          className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                        >
                          <CheckCircle size={12} /> Bruk forslag
                        </button>
                      </div>
                    )}

                    {/* Learning Outcomes - Knowledge */}
                    {improvements.learning_outcomes?.knowledge?.some((k: any) => k.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Kunnskaper</h5>
                        {improvements.learning_outcomes.knowledge
                          .filter((k: any) => k.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'knowledge', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Learning Outcomes - Skills */}
                    {improvements.learning_outcomes?.skills?.some((s: any) => s.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Ferdigheter</h5>
                        {improvements.learning_outcomes.skills
                          .filter((s: any) => s.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'skills', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Learning Outcomes - Competence */}
                    {improvements.learning_outcomes?.competence?.some((c: any) => c.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Læringsutbytte - Generell kompetanse</h5>
                        {improvements.learning_outcomes.competence
                          .filter((c: any) => c.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 text-xs">Nåværende ({item.index + 1}):</span>
                                  <p className="text-gray-700">{item.current}</p>
                                </div>
                                <div>
                                  <span className="text-green-600 text-xs">Forslag:</span>
                                  <p className="text-green-700">{item.suggested}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('learning_outcomes', item.suggested, 'competence', item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Content Modules */}
                    {improvements.content_modules?.some((m: any) => m.needs_improvement) && (
                      <div className="p-3 bg-white border border-amber-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Innholdsmoduler</h5>
                        {improvements.content_modules
                          .filter((m: any) => m.needs_improvement)
                          .map((item: any, idx: number) => (
                            <div key={idx} className="mt-2 p-2 bg-gray-50 rounded">
                              <div className="text-sm">
                                <span className="text-gray-500 text-xs">Modul {item.index + 1}:</span>
                                <div className="grid grid-cols-2 gap-3 mt-1">
                                  <div>
                                    <span className="text-gray-500 text-xs">Nåværende:</span>
                                    <p className="text-gray-700">{item.current?.title || '(uten tittel)'}</p>
                                  </div>
                                  <div>
                                    <span className="text-green-600 text-xs">Forslag:</span>
                                    <p className="text-green-700">{item.suggested?.title}</p>
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                              <button
                                type="button"
                                onClick={() => applyImprovement('content_modules', item.suggested, undefined, item.index)}
                                className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                              >
                                <CheckCircle size={12} /> Bruk forslag
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Metadata - EU Standards */}
                    {(improvements.metadata?.eqf_level?.needs_improvement ||
                      improvements.metadata?.esco_skills?.needs_improvement ||
                      improvements.metadata?.isced_code?.needs_improvement ||
                      improvements.metadata?.keywords?.needs_improvement) && (
                      <div className="p-3 bg-white border border-purple-200 rounded-lg">
                        <h5 className="font-medium text-sm mb-2 text-purple-800">Metadata (EU-standarder)</h5>

                        {/* EQF Level */}
                        {improvements.metadata?.eqf_level?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">EQF-nivå</span>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                              <div>
                                <span className="text-gray-500 text-xs">Nåværende:</span>
                                <p className="text-gray-700">{improvements.metadata.eqf_level.current || '(ikke satt)'}</p>
                              </div>
                              <div>
                                <span className="text-green-600 text-xs">Forslag:</span>
                                <p className="text-green-700 font-medium">Nivå {improvements.metadata.eqf_level.suggested}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{improvements.metadata.eqf_level.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.eqf_level.suggested, 'eqf_level')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* ESCO Skills */}
                        {improvements.metadata?.esco_skills?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">ESCO-ferdigheter</span>
                            <div className="mt-1">
                              <span className="text-gray-500 text-xs">Nåværende:</span>
                              <p className="text-gray-700 text-sm">
                                {(improvements.metadata.esco_skills.current?.length > 0)
                                  ? improvements.metadata.esco_skills.current.map((s: any) => typeof s === 'string' ? s : s.title || s.name || JSON.stringify(s)).join(', ')
                                  : '(ingen)'}
                              </p>
                            </div>
                            <div className="mt-2">
                              <span className="text-green-600 text-xs">Forslag:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {improvements.metadata.esco_skills.suggested?.map((skill: string, idx: number) => (
                                  <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{improvements.metadata.esco_skills.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.esco_skills.suggested, 'esco_skills')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* ISCED Code */}
                        {improvements.metadata?.isced_code?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">ISCED-F 2013 kode</span>
                            <div className="grid grid-cols-2 gap-3 mt-1">
                              <div>
                                <span className="text-gray-500 text-xs">Nåværende:</span>
                                <p className="text-gray-700">{improvements.metadata.isced_code.current || '(ikke satt)'}</p>
                              </div>
                              <div>
                                <span className="text-green-600 text-xs">Forslag:</span>
                                <p className="text-green-700 font-medium">{improvements.metadata.isced_code.suggested}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{improvements.metadata.isced_code.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.isced_code.suggested, 'isced_code')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}

                        {/* Keywords */}
                        {improvements.metadata?.keywords?.needs_improvement && (
                          <div className="mt-2 p-2 bg-purple-50 rounded">
                            <span className="text-purple-600 text-xs font-medium">Nøkkelord</span>
                            <div className="mt-1">
                              <span className="text-gray-500 text-xs">Nåværende:</span>
                              <p className="text-gray-700 text-sm">
                                {(improvements.metadata.keywords.current?.length > 0)
                                  ? improvements.metadata.keywords.current.join(', ')
                                  : '(ingen)'}
                              </p>
                            </div>
                            <div className="mt-2">
                              <span className="text-green-600 text-xs">Forslag:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {improvements.metadata.keywords.suggested?.map((kw: string, idx: number) => (
                                  <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{improvements.metadata.keywords.reason}</p>
                            <button
                              type="button"
                              onClick={() => applyImprovement('metadata', improvements.metadata.keywords.suggested, 'keywords')}
                              className="mt-2 text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            >
                              <CheckCircle size={12} /> Bruk forslag
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No improvements needed message */}
                    {!improvements.target_group?.needs_improvement &&
                     !improvements.admission_requirements?.needs_improvement &&
                     !improvements.teaching_learning_methods?.needs_improvement &&
                     !improvements.description?.needs_improvement &&
                     !improvements.assessment_criteria?.needs_improvement &&
                     !improvements.learning_outcomes?.knowledge?.some((k: any) => k.needs_improvement) &&
                     !improvements.learning_outcomes?.skills?.some((s: any) => s.needs_improvement) &&
                     !improvements.learning_outcomes?.competence?.some((c: any) => c.needs_improvement) &&
                     !improvements.content_modules?.some((m: any) => m.needs_improvement) &&
                     !improvements.metadata?.eqf_level?.needs_improvement &&
                     !improvements.metadata?.esco_skills?.needs_improvement &&
                     !improvements.metadata?.isced_code?.needs_improvement &&
                     !improvements.metadata?.keywords?.needs_improvement && (
                      <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                        <CheckCircle size={16} className="inline mr-2" />
                        Studieplanen ser bra ut! Ingen spesifikke justeringer anbefalt.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Approval Status Section - integrated action area */}
          {(id || createdPlanId) && validationResult.decision && (
            <div className={`mt-4 p-4 rounded-lg border ${
              validationResult.decision === 'approved'
                ? 'bg-green-50 border-green-200'
                : validationResult.decision === 'rejected_with_revisions'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
            }`}>
              {/* Status message - only show for non-approved decisions (approved has its own prominent section below) */}
              {validationResult.decision !== 'approved' && (
                <div className="flex items-start gap-3 mb-3">
                  {validationResult.decision === 'rejected_with_revisions' ? (
                    <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  ) : (
                    <X className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                  )}
                  <div>
                    <p className={`font-medium ${
                      validationResult.decision === 'rejected_with_revisions' ? 'text-yellow-900' :
                      'text-red-900'
                    }`}>
                      {validationResult.decision === 'rejected_with_revisions' ? 'Krever begrunnelse for innsending' :
                       'Krever forbedring'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      validationResult.decision === 'rejected_with_revisions' ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      {validationResult.feedback?.approval_status?.message ||
                       (validationResult.decision === 'rejected_with_revisions'
                         ? 'Studieplanen kan sendes inn med begrunnelse for manuell vurdering.'
                         : 'Studieplanen må forbedres før den kan sendes inn.')}
                    </p>
                  </div>
                </div>
              )}

              {/* Self-approve button for approved plans */}
              {validationResult.decision === 'approved' && currentUser?.role === 'member' && (
                <div className="mt-4 pt-4 border-t-2 border-green-300 bg-green-100 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-green-900">Klar for godkjenning!</p>
                      <p className="text-sm text-green-700">Studieplanen oppfyller alle krav og kan selvgodkjennes.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSelfApproveModal(true)}
                      className="btn bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 shadow-lg"
                    >
                      <CheckCircle size={18} />
                      Selvgodkjenn
                    </button>
                  </div>
                </div>
              )}

              {/* Admin message for plans with score 70-79 - they can approve directly */}
              {validationResult.decision === 'rejected_with_revisions' && validationResult.score >= 70 && (currentUser?.role === 'admin' || currentUser?.role === 'systemadmin') && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={16} />
                    <p className="text-sm">
                      Som {currentUser?.role === 'systemadmin' ? 'systemadministrator' : 'studieforbund-administrator'} kan du godkjenne denne studieplanen direkte med "Godkjenn"-knappen nederst.
                    </p>
                  </div>
                </div>
              )}

              {/* Inline justification form for rejected_with_revisions plans - only show for members */}
              {validationResult.decision === 'rejected_with_revisions' && currentUser?.role === 'member' && (
                <div className="mt-3 pt-3 border-t border-yellow-200">
                  <p className="text-sm text-yellow-800 mb-3">
                    Du kan sende studieplanen til studieforbundet for manuell vurdering.
                    Oppgi en begrunnelse for hvorfor planen bør godkjennes til tross for KI-tilbakemeldingen.
                  </p>

                  <div className="mb-3">
                    <label className="label text-sm font-medium text-yellow-900 mb-1">
                      Begrunnelse for innsending *
                    </label>
                    <textarea
                      className="input bg-white border-yellow-300 focus:border-yellow-500"
                      rows={3}
                      placeholder="Forklar hvorfor studieplanen bør vurderes av studieforbundet..."
                      value={submissionJustification}
                      onChange={(e) => setSubmissionJustification(e.target.value)}
                    />
                    <p className="text-xs text-yellow-700 mt-1">
                      Minimum 50 tegn ({submissionJustification.length}/50)
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmitForReview}
                    disabled={submittingForReview || submissionJustification.trim().length < 50}
                    className="btn bg-yellow-600 hover:bg-yellow-700 text-white flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {submittingForReview ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <AlertTriangle size={18} />
                    )}
                    Send til vurdering hos studieforbundet
                  </button>
                </div>
              )}

              {/* Message for fully rejected plans */}
              {validationResult.decision === 'rejected' && (
                <p className="text-sm text-red-700 mt-2">
                  Bruk KI-forslagene ovenfor for å forbedre studieplanen, og kjør deretter ny validering.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <form className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Grunnleggende informasjon</h2>

          <div className="space-y-4">
            <div>
              <label className="label">Tittel *</label>
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">Beskrivelse *</label>
              <textarea
                className="input"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Kompetansepoeng (Tilsv.ECTS) *</label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  step="0.5"
                  value={formData.ects_credits}
                  onChange={(e) => setFormData({ ...formData, ects_credits: parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div>
                <label className="label">Estimerte timer *</label>
                <input
                  type="number"
                  className="input"
                  min="4"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Anbefalt: {formData.ects_credits * 25}-{formData.ects_credits * 30} timer (minimum 4 timer for Kompetanseattest)
                </p>
              </div>
            </div>

            {/* Target group with ESCO occupation matching */}
            <OccupationMatcher
              value={formData.target_group || ''}
              onChange={(value) => {
                setFormData({ ...formData, target_group: value });
                setHasUnsavedChanges(true);
              }}
              matchedOccupations={targetGroupOccupations}
              onOccupationsChange={(occupations) => {
                setTargetGroupOccupations(occupations);
                setHasUnsavedChanges(true);
              }}
              label="Målgruppe"
              placeholder="Beskriv hvem kurset er rettet mot, f.eks. 'Voksne uten formell utdanning innen fagfeltet' eller 'Ansatte i helsesektoren med grunnleggende IKT-kompetanse'"
              helpText="Yrkesroller identifisert i teksten kobles automatisk til ESCO-klassifiseringen."
              rows={2}
            />

            {/* Admission requirements with ESCO occupation matching */}
            <OccupationMatcher
              value={formData.admission_requirements || ''}
              onChange={(value) => {
                setFormData({ ...formData, admission_requirements: value });
                setHasUnsavedChanges(true);
              }}
              matchedOccupations={admissionOccupations}
              onOccupationsChange={(occupations) => {
                setAdmissionOccupations(occupations);
                setHasUnsavedChanges(true);
              }}
              label="Opptakskrav"
              placeholder="Eventuelle forkunnskaper eller formelle krav, f.eks. 'Ingen formelle opptakskrav' eller 'Grunnleggende datakunnskaper'"
              helpText="Eventuelle yrkeskrav identifiseres og kobles til ESCO."
              rows={2}
            />

            <div>
              <label className="label">Undervisnings- og læringsformer</label>
              <textarea
                className="input"
                rows={3}
                value={formData.teaching_learning_methods || ''}
                onChange={(e) => setFormData({ ...formData, teaching_learning_methods: e.target.value })}
                placeholder="Beskriv hvordan undervisningen gjennomføres, f.eks. 'Nettbasert læring med videoforelesninger, praktiske øvelser og gruppearbeid'"
              />
            </div>
          </div>
        </div>

        {/* Learning Outcomes with ESCO and Bloom's Taxonomy */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Læringsutbytte</h2>
          <p className="text-sm text-gray-500 mb-4">
            Hvert læringsutbytte analyseres for Bloom's taksonomi-nivå og kobles automatisk til ESCO-ferdigheter.
          </p>
          <LearningOutcomeEditor
            learning_outcomes={formData.learning_outcomes}
            onChange={(outcomes) => {
              setFormData({ ...formData, learning_outcomes: outcomes });
              setHasUnsavedChanges(true);
              setNeedsRevalidation(true);
            }}
            escoMappings={outcomeEscoMappings}
            onESCOMappingsChange={(mappings) => {
              setOutcomeEscoMappings(mappings);
              setHasUnsavedChanges(true);
            }}
            eqfLevel={parseInt(formData.metadata?.eqf_level || '5', 10)}
            ectsCredits={formData.ects_credits}
            showAutoSuggest={true}
            studyPlanContext={`${formData.title || ''} - ${formData.description || ''}`}
          />
        </div>

        {/* Content Modules */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Innholdsmoduler</h2>
            <button
              type="button"
              onClick={() => setShowModuleSection(!showModuleSection)}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800"
            >
              <Layers size={16} />
              {showModuleSection ? 'Skjul detaljerte moduler' : 'Vis detaljerte moduler'}
            </button>
          </div>

          {/* Simple text-based modules (legacy) */}
          {!showModuleSection && (
            <>
              <div className="space-y-2 mb-4">
                <textarea
                  className="input w-full min-h-[80px] resize-y"
                  placeholder="Skriv innholdsmodul her..."
                  value={moduleInput}
                  onChange={(e) => setModuleInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      addModule();
                    }
                  }}
                />
                <div className="flex justify-end">
                  <button type="button" onClick={addModule} className="btn btn-secondary">
                    Legg til modul
                  </button>
                </div>
                <p className="text-xs text-gray-500">Tips: Trykk Ctrl+Enter for å legge til raskt</p>
              </div>
              <ul className="space-y-2">
                {formData.content_modules.map((module, index) => (
                  <li key={index} className="bg-gray-50 p-3 rounded">
                    {editingItem?.field === 'module' && editingItem.index === index ? (
                      <div className="space-y-2">
                        <textarea
                          className="input w-full min-h-[100px] resize-y"
                          value={editingItem.value}
                          onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingItem(null);
                            }
                          }}
                          autoFocus
                          placeholder="Skriv innholdsmodul her..."
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => setEditingItem(null)}
                            className="btn btn-secondary text-sm py-1 px-3"
                          >
                            Avbryt
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              updateModule(index, editingItem.value);
                              setEditingItem(null);
                            }}
                            className="btn btn-primary text-sm py-1 px-3"
                          >
                            Lagre
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className="flex-1 cursor-pointer hover:text-kf-primary whitespace-pre-wrap"
                          onClick={() => setEditingItem({field: 'module', index, value: module})}
                          title="Klikk for å redigere"
                        >
                          {module}
                        </span>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setEditingItem({field: 'module', index, value: module})}
                            className="text-gray-500 hover:text-kf-primary p-1"
                            title="Rediger"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeModule(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Fjern"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Detailed module editor (for modular completion) */}
          {showModuleSection && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Bruk detaljerte moduler for å tillate kandidater å fullføre deler av studieplanen separat.
                Hver modul kan ha egne læringsutbytte, kompetansepoeng og litteraturreferanser.
              </p>
              <ModuleEditor
                studyPlanId={id}
                modules={modules}
                onChange={setModules}
                parentLearningOutcomes={formData.learning_outcomes}
                parentEscoMappings={outcomeEscoMappings}
                totalEcts={formData.ects_credits}
                totalHours={formData.estimated_hours}
                disabled={studyPlanStatus === 'approved'}
              />
            </div>
          )}
        </div>

        {/* Assessment */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Vurdering</h2>
          <div>
            <label className="label">Vurderingskriterier *</label>
            <textarea
              className="input"
              rows={4}
              value={formData.assessment_criteria}
              onChange={(e) => setFormData({ ...formData, assessment_criteria: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Metadata */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Metadata</h2>
          <div className="space-y-4">
            <div>
              <label className="label">EQF-nivå</label>
              <select
                className="input"
                value={formData.metadata.eqf_level}
                onChange={(e) => setFormData({
                  ...formData,
                  metadata: { ...formData.metadata, eqf_level: e.target.value as any }
                })}
              >
                {['1', '2', '3', '4', '5', '6', '7', '8'].map((level) => (
                  <option key={level} value={level}>Nivå {level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">ISCED-F Fagområde (valgfritt)</label>
              <select
                className="input"
                value={formData.isced_f_code || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  isced_f_code: e.target.value || null
                })}
              >
                <option value="">Velg fagområde...</option>
                {/* 00 - Generelle programmer */}
                <optgroup label="00 - Generelle programmer">
                  <option value="0011">0011 - Grunnleggende programmer / Basic programmes</option>
                  <option value="0021">0021 - Lese- og skriveopplæring / Literacy and numeracy</option>
                  <option value="0031">0031 - Personlig utvikling / Personal skills</option>
                </optgroup>
                {/* 01 - Utdanning */}
                <optgroup label="01 - Utdanning / Education">
                  <option value="0111">0111 - Utdanningsvitenskap / Education science</option>
                  <option value="0112">0112 - Førskolelærerutdanning / Training for pre-school teachers</option>
                  <option value="0113">0113 - Lærerutdanning uten fagspesialisering / Teacher training without subject</option>
                  <option value="0114">0114 - Lærerutdanning med fagspesialisering / Teacher training with subject</option>
                </optgroup>
                {/* 02 - Humaniora og kunst */}
                <optgroup label="02 - Humaniora og kunst / Arts and humanities">
                  <option value="0211">0211 - Audiovisuelle teknikker / Audio-visual techniques</option>
                  <option value="0212">0212 - Mote, interiør og industridesign / Fashion, interior and industrial design</option>
                  <option value="0213">0213 - Kunst / Fine arts</option>
                  <option value="0214">0214 - Håndverk / Handicrafts</option>
                  <option value="0215">0215 - Musikk og utøvende kunst / Music and performing arts</option>
                  <option value="0221">0221 - Religion og teologi / Religion and theology</option>
                  <option value="0222">0222 - Historie og arkeologi / History and archaeology</option>
                  <option value="0223">0223 - Filosofi og etikk / Philosophy and ethics</option>
                  <option value="0231">0231 - Språk / Languages</option>
                  <option value="0232">0232 - Litteratur og lingvistikk / Literature and linguistics</option>
                </optgroup>
                {/* 03 - Samfunnsfag, journalistikk og informasjon */}
                <optgroup label="03 - Samfunnsfag / Social sciences">
                  <option value="0311">0311 - Økonomi / Economics</option>
                  <option value="0312">0312 - Statsvitenskap / Political sciences</option>
                  <option value="0313">0313 - Psykologi / Psychology</option>
                  <option value="0314">0314 - Sosiologi og kulturstudier / Sociology and cultural studies</option>
                  <option value="0321">0321 - Journalistikk og reportasje / Journalism and reporting</option>
                  <option value="0322">0322 - Bibliotek og informasjon / Library and information</option>
                </optgroup>
                {/* 04 - Økonomi, administrasjon og jus */}
                <optgroup label="04 - Økonomi, administrasjon og jus / Business and law">
                  <option value="0411">0411 - Regnskap og skatt / Accounting and taxation</option>
                  <option value="0412">0412 - Finans, bank og forsikring / Finance, banking and insurance</option>
                  <option value="0413">0413 - Ledelse og administrasjon / Management and administration</option>
                  <option value="0414">0414 - Markedsføring og reklame / Marketing and advertising</option>
                  <option value="0415">0415 - Sekretær og kontorarbeid / Secretarial and office work</option>
                  <option value="0416">0416 - Engroshandel og detaljhandel / Wholesale and retail sales</option>
                  <option value="0417">0417 - Arbeidslivsferdigheter / Work skills</option>
                  <option value="0421">0421 - Jus / Law</option>
                </optgroup>
                {/* 05 - Naturvitenskap, matematikk og statistikk */}
                <optgroup label="05 - Naturvitenskap / Natural sciences">
                  <option value="0511">0511 - Biologi / Biology</option>
                  <option value="0512">0512 - Biokjemi / Biochemistry</option>
                  <option value="0521">0521 - Miljøvitenskap / Environmental sciences</option>
                  <option value="0522">0522 - Naturmiljø og dyreliv / Natural environments and wildlife</option>
                  <option value="0531">0531 - Kjemi / Chemistry</option>
                  <option value="0532">0532 - Geofag / Earth sciences</option>
                  <option value="0533">0533 - Fysikk / Physics</option>
                  <option value="0541">0541 - Matematikk / Mathematics</option>
                  <option value="0542">0542 - Statistikk / Statistics</option>
                </optgroup>
                {/* 06 - IKT */}
                <optgroup label="06 - Informasjons- og kommunikasjonsteknologi / ICT">
                  <option value="0611">0611 - Databehandling / Computer use</option>
                  <option value="0612">0612 - Database og nettverksdesign / Database and network design</option>
                  <option value="0613">0613 - Programvareutvikling / Software development</option>
                </optgroup>
                {/* 07 - Ingeniørfag, produksjon og bygg */}
                <optgroup label="07 - Ingeniørfag og bygg / Engineering and construction">
                  <option value="0711">0711 - Kjemiteknikk / Chemical engineering</option>
                  <option value="0712">0712 - Miljøteknologi / Environmental protection technology</option>
                  <option value="0713">0713 - Elektrisitet og energi / Electricity and energy</option>
                  <option value="0714">0714 - Elektronikk og automasjon / Electronics and automation</option>
                  <option value="0715">0715 - Maskinteknikk / Mechanics and metal trades</option>
                  <option value="0716">0716 - Kjøretøy og transport / Motor vehicles, ships and aircraft</option>
                  <option value="0721">0721 - Mat og næringsmidler / Food processing</option>
                  <option value="0722">0722 - Materialer / Materials</option>
                  <option value="0723">0723 - Tekstil og skotøy / Textiles</option>
                  <option value="0724">0724 - Bergverk og utvinning / Mining and extraction</option>
                  <option value="0731">0731 - Arkitektur og byplanlegging / Architecture and urban planning</option>
                  <option value="0732">0732 - Bygg og anlegg / Building and civil engineering</option>
                </optgroup>
                {/* 08 - Landbruk, skogbruk, fiske og veterinær */}
                <optgroup label="08 - Landbruk og veterinær / Agriculture and veterinary">
                  <option value="0811">0811 - Jordbruk / Crop and livestock production</option>
                  <option value="0812">0812 - Hagebruk / Horticulture</option>
                  <option value="0821">0821 - Skogbruk / Forestry</option>
                  <option value="0831">0831 - Fiske / Fisheries</option>
                  <option value="0841">0841 - Veterinær / Veterinary</option>
                </optgroup>
                {/* 09 - Helse og velferd */}
                <optgroup label="09 - Helse og velferd / Health and welfare">
                  <option value="0911">0911 - Tannhelse / Dental studies</option>
                  <option value="0912">0912 - Medisin / Medicine</option>
                  <option value="0913">0913 - Sykepleie og jordmor / Nursing and midwifery</option>
                  <option value="0914">0914 - Medisinsk diagnostikk / Medical diagnostic and treatment</option>
                  <option value="0915">0915 - Terapi og rehabilitering / Therapy and rehabilitation</option>
                  <option value="0916">0916 - Farmasi / Pharmacy</option>
                  <option value="0917">0917 - Tradisjonell medisin / Traditional and complementary medicine</option>
                  <option value="0921">0921 - Eldreomsorg og funksjonshemmede / Care of the elderly and disabled</option>
                  <option value="0922">0922 - Barnevern og ungdomsarbeid / Child care and youth services</option>
                  <option value="0923">0923 - Sosialt arbeid / Social work and counselling</option>
                </optgroup>
                {/* 10 - Tjenester */}
                <optgroup label="10 - Tjenester / Services">
                  <option value="1011">1011 - Husholdningstjenester / Domestic services</option>
                  <option value="1012">1012 - Hår og skjønnhet / Hair and beauty</option>
                  <option value="1013">1013 - Hotell, restaurant og catering / Hotel, restaurants and catering</option>
                  <option value="1014">1014 - Sport og fritid / Sports</option>
                  <option value="1015">1015 - Reiseliv og turisme / Travel, tourism and leisure</option>
                  <option value="1021">1021 - Renhold og renovasjon / Community sanitation</option>
                  <option value="1022">1022 - Arbeidsmiljø og sikkerhet / Occupational health and safety</option>
                  <option value="1031">1031 - Militære og forsvarsfag / Military and defence</option>
                  <option value="1032">1032 - Sikkerhetstjenester / Protection of persons and property</option>
                  <option value="1041">1041 - Transporttjenester / Transport services</option>
                </optgroup>
              </select>
              <p className="text-sm text-gray-600 mt-1">
                🇪🇺 ISCED-F 2013 klassifisering brukes i Europass Digital Credentials for europeisk anerkjennelse
              </p>
            </div>

            {/* Keywords Section */}
            <div>
              <label className="label">Nøkkelord</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.metadata?.keywords || []).map((keyword: string, idx: number) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-sm border border-purple-200"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => {
                        const newKeywords = [...(formData.metadata?.keywords || [])];
                        newKeywords.splice(idx, 1);
                        setFormData({
                          ...formData,
                          metadata: { ...formData.metadata, keywords: newKeywords }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      className="hover:text-purple-900"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Legg til nøkkelord..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      const value = input.value.trim();
                      if (value && !(formData.metadata?.keywords || []).includes(value)) {
                        setFormData({
                          ...formData,
                          metadata: {
                            ...formData.metadata,
                            keywords: [...(formData.metadata?.keywords || []), value]
                          }
                        });
                        setHasUnsavedChanges(true);
                        input.value = '';
                      }
                    }
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Trykk Enter for å legge til. Nøkkelord brukes for søk og kategorisering.
              </p>
            </div>

            {/* ESCO Skills Section - Collected from Learning Outcomes */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <label className="label flex items-center gap-2">
                <Link2 size={16} className="text-blue-600" />
                <span>ESCO-ferdigheter</span>
              </label>

              <p className="text-sm text-gray-600 mb-3">
                🇪🇺 Ferdigheter fra EUs ESCO-klassifisering, koblet til læringsutbyttene ovenfor.
              </p>

              {/* Collected ESCO skills from learning outcome mappings */}
              {(() => {
                // ESCO v2.2: Only show confirmed skills (what will actually be saved)
                const confirmedMappings = filterConfirmedESCOMappings(outcomeEscoMappings);

                // Collect all unique ESCO skills from outcome mappings
                const collectedSkills = new Map<string, { title: string; skillType?: string; category: string }>();

                confirmedMappings.forEach(mapping => {
                  mapping.escoSkills.forEach(skill => {
                    if (!collectedSkills.has(skill.uri)) {
                      collectedSkills.set(skill.uri, {
                        title: skill.title,
                        skillType: skill.skillType,
                        category: mapping.outcomeType
                      });
                    }
                  });
                });

                const skillsList = Array.from(collectedSkills.entries());
                const totalCount = skillsList.length + selectedEscoSkills.length;

                return (
                  <>
                    {skillsList.length > 0 ? (
                      <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-blue-700 mb-2">
                          Fra læringsutbytter ({skillsList.length}):
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {skillsList.map(([uri, skill]) => (
                            <span
                              key={uri}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${
                                skill.category === 'knowledge'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : skill.category === 'skills'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-violet-50 text-violet-700 border-violet-200'
                              }`}
                              title={`Fra ${skill.category === 'knowledge' ? 'kunnskap' : skill.category === 'skills' ? 'ferdigheter' : 'kompetanse'}`}
                            >
                              <Link2 size={10} />
                              {skill.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Ingen ESCO-ferdigheter koblet ennå.</strong> Bruk "Auto-foreslå ESCO"-knappen under Læringsutbytte for å koble ferdigheter automatisk.
                        </p>
                      </div>
                    )}

                    {/* Additional manually added skills */}
                    {selectedEscoSkills.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Manuelt lagt til ({selectedEscoSkills.length}):
                        </p>
                        <div className="space-y-1.5">
                          {selectedEscoSkills.map((skill) => (
                            <div
                              key={skill.uri}
                              className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm"
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Link2 size={12} className="text-gray-400 flex-shrink-0" />
                                <span className="truncate">{skill.title}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeEscoSkill(skill.uri)}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    {totalCount > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        Totalt {totalCount} ESCO-ferdigheter vil inkluderes i attesten.
                      </p>
                    )}
                  </>
                );
              })()}

              {/* Expandable section to add more skills */}
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <Plus size={14} />
                  Legg til flere ESCO-ferdigheter manuelt
                </summary>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="relative">
                    <input
                      type="text"
                      className="input pr-8"
                      placeholder="Søk etter ESCO skills..."
                      value={escoSearchQuery}
                      onChange={(e) => setEscoSearchQuery(e.target.value)}
                    />
                    {escoSearching && (
                      <Loader2 className="absolute right-2 top-1/2 transform -translate-y-1/2 animate-spin text-gray-400" size={16} />
                    )}
                  </div>

                  {/* Search Results */}
                  {escoSearchResults.length > 0 && (
                    <div className="mt-2 border border-gray-300 rounded-lg bg-white max-h-48 overflow-y-auto">
                      {escoSearchResults.map((skill) => (
                        <button
                          key={skill.uri}
                          type="button"
                          onClick={() => addEscoSkill(skill)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-200 last:border-b-0"
                        >
                          <div className="font-medium text-sm">{skill.title}</div>
                          <div className="text-xs text-gray-500">
                            {skill.skillType}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            </div>

            <div>
              <label className="label">Vurderingsmetoder</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Legg til metode..."
                  value={assessmentMethodInput}
                  onChange={(e) => setAssessmentMethodInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAssessmentMethod();
                    }
                  }}
                />
                <button type="button" onClick={addAssessmentMethod} className="btn btn-secondary">
                  Legg til
                </button>
              </div>
              <ul className="space-y-1">
                {formData.metadata.assessment_methods?.map((method, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm gap-2">
                    {editingItem?.field === 'assessment' && editingItem.index === index ? (
                      <input
                        type="text"
                        className="input flex-1 text-sm"
                        value={editingItem.value}
                        onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                        onBlur={() => {
                          updateAssessmentMethod(index, editingItem.value);
                          setEditingItem(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateAssessmentMethod(index, editingItem.value);
                            setEditingItem(null);
                          } else if (e.key === 'Escape') {
                            setEditingItem(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="flex-1 cursor-pointer hover:text-kf-primary"
                        onClick={() => setEditingItem({field: 'assessment', index, value: method})}
                        title="Klikk for å redigere"
                      >
                        {method}
                      </span>
                    )}
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingItem({field: 'assessment', index, value: method})}
                        className="text-gray-500 hover:text-kf-primary p-1"
                        title="Rediger"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            metadata: {
                              ...formData.metadata,
                              assessment_methods: formData.metadata.assessment_methods?.filter((_, i) => i !== index),
                            },
                          });
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Fjern"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Literature */}
        {id && (
          <div className="card">
            <LiteratureManager
              studyPlanId={id}
              readOnly={studyPlanStatus === 'approved' && !needsRevalidation}
              onLiteratureChange={() => {
                if (studyPlanStatus === 'approved') {
                  setNeedsRevalidation(true);
                  setValidationResult(null);
                }
              }}
            />
          </div>
        )}

        {/* Translation - only for approved study plans */}
        {id && studyPlanStatus === 'approved' && (
          <div className="card">
            <TranslationEditor
              studyPlan={{
                id,
                title: formData.title,
                description: formData.description,
                target_group: formData.target_group || '',
                admission_requirements: formData.admission_requirements || '',
                teaching_learning_methods: formData.teaching_learning_methods || '',
                assessment_criteria: formData.assessment_criteria,
                learning_outcomes: formData.learning_outcomes,
                content_modules: formData.content_modules,
                status: studyPlanStatus
              }}
            />
          </div>
        )}

        {/* Error message near save button */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Validation confirmation info box - shows when user clicks KI-validering with unsaved changes */}
        {showValidationConfirm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Save size={20} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-1">Lagring av endringer</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Du har ulagrede endringer. Når du kjører KI-validering, vil endringene dine lagres automatisk først,
                  og deretter valideres den oppdaterte studieplanen.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleValidate}
                    className="btn bg-blue-600 hover:bg-blue-700 text-white text-sm flex items-center gap-2"
                  >
                    <Sparkles size={16} />
                    Lagre og valider
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowValidationConfirm(false)}
                    className="btn btn-secondary text-sm"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 flex-wrap">
          <button
            type="button"
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="btn btn-secondary flex items-center gap-2"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Lagre utkast
          </button>

          {/* Show "Send til studieforbund" for members ONLY when validation has run AND score < 70 */}
          {currentUser?.role === 'member' && validationRunThisSession && validationResult && validationResult.score < 70 && !needsRevalidation && !hasUnsavedChanges && (
            <div className="relative group">
              <button
                type="button"
                onClick={() => handleSave('submitted')}
                disabled={saving}
                className="btn btn-primary flex items-center gap-2 bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                Send til studieforbund
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="flex items-center gap-1">
                  <Info size={12} />
                  KI-score er under 70% - krever manuell godkjenning
                </div>
              </div>
            </div>
          )}

          {/* Show self-approval button for members with passing KI score - only after validation run this session */}
          {(id || createdPlanId) && currentUser?.role === 'member' && validationResult && validationResult.score >= 70 && !needsRevalidation && !hasUnsavedChanges && validationRunThisSession && (
            <button
              type="button"
              onClick={() => setShowSelfApproveModal(true)}
              disabled={approving || saving}
              className="btn btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
              Godkjenn selv
            </button>
          )}

          {/* Show approval button for admin/systemadmin when score >= 70 */}
          {(id || createdPlanId) && validationResult && validationResult.score >= 70 && currentUser && (currentUser.role === 'admin' || currentUser.role === 'systemadmin') && !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowApproveModal(true)}
              disabled={approving || saving}
              className="btn btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
              Godkjenn
            </button>
          )}

          {/* Show OVERRIDE button for admin/systemadmin when score < 70 */}
          {(id || createdPlanId) && validationResult && validationResult.score < 70 && currentUser && (currentUser.role === 'admin' || currentUser.role === 'systemadmin') && !needsRevalidation && !hasUnsavedChanges && (
            <button
              type="button"
              onClick={() => setShowOverrideModal(true)}
              disabled={approving || saving}
              className="btn flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {approving ? <Loader2 className="animate-spin" size={18} /> : <AlertTriangle size={18} />}
              Overprøv KI og godkjenn
            </button>
          )}

          <button
            type="button"
            onClick={handleValidateClick}
            disabled={validating || !(id || createdPlanId)}
            title={!(id || createdPlanId) ? 'Lagre utkast først for å kjøre KI-validering' : ''}
            className={`btn flex items-center gap-2 ${
              !(id || createdPlanId)
                ? 'btn-secondary opacity-50 cursor-not-allowed'
                : 'btn-primary bg-kf-accent hover:bg-kf-accent'
            }`}
          >
            {validating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            KI-validering
          </button>

          <button
            type="button"
            onClick={() => navigate('/study-plans')}
            className="btn btn-secondary"
          >
            Avbryt
          </button>
        </div>
      </form>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Godkjenn studieplan</h3>

            {/* KI-validering resultat (ikke-redigerbar) */}
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
              <h4 className="font-semibold text-sm text-gray-900 mb-2">KI-VALIDERING</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Score:</span>
                  <span className={`font-bold ${
                    (Number(validationResult?.score) || 0) >= 70 ? 'text-green-600' : 'text-kf-warning'
                  }`}>
                    {Number(validationResult?.score || 0).toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Tilbakemelding:</span>
                  <p className="text-gray-800 mt-1">{validationResult?.feedback.summary}</p>
                </div>
              </div>
            </div>

            {/* Check if score is below threshold */}
            {(Number(validationResult?.score) || 0) < 70 ? (
              <>
                <p className="text-gray-600 mb-4">
                  Du må begrunne hvorfor du godkjenner denne studieplanen til tross for lav AI-score. Begrunnelsen vil bli lagret
                  sammen med KI-valideringsresultatet og sendt til brukeren.
                </p>

                <div className="mb-4">
                  <label className="label text-sm font-medium mb-2">
                    Begrunnelse for godkjenning (påkrevd) *
                  </label>
                  <textarea
                    className="input"
                    rows={4}
                    placeholder="Skriv inn hvorfor du godkjenner studieplanen til tross for KI-tilbakemeldingen..."
                    value={approveComment}
                    onChange={(e) => setApproveComment(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Eksempel: "Studieplanen oppfyller minimumskrav for voksenopplæring. Manglene som KI-en påpeker er mindre vesentlige og vil bli adressert i neste revisjon."
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  Er du sikker på at du vil godkjenne denne studieplanen? Studieplanen oppfyller kvalitetskravene basert på KI-valideringen.
                </p>

                <div className="mb-4">
                  <label className="label text-sm font-medium mb-2">
                    Ytterligere merknader til godkjenningen:
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Valgfritt: Legg til merknader til godkjenningen..."
                    value={approveComment}
                    onChange={(e) => setApproveComment(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={approving || ((Number(validationResult?.score) || 0) < 70 && !approveComment.trim())}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                Godkjenn
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveComment('');
                }}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-Approve Modal */}
      {showSelfApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Selvgodkjenn studieplan</h3>

            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <p className="font-medium text-green-900">KI-validering bestått</p>
              </div>
              <p className="text-sm text-green-800">
                Studieplanen din har oppnådd en KI-score på <strong>{Number(validationResult?.score || 0).toFixed(1)}%</strong>,
                som er over terskelverdien. Du kan nå godkjenne planen selv uten å sende den til studieforbundet.
              </p>
            </div>

            <p className="text-gray-600 mb-4">
              Ved selvgodkjenning vil det genereres en unik verifiseringslenke som kan deles offentlig.
              Du kan også velge å sende planen til studieforbundet for manuell vurdering i stedet.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleSelfApprove}
                disabled={approving}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                Godkjenn selv
              </button>
              <button
                onClick={() => setShowSelfApproveModal(false)}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Override Modal - for admin/systemadmin when score < 70 */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={24} />
              Overprøv KI-validering
            </h3>

            {/* Warning box */}
            <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-900 mb-1">Viktig informasjon</p>
                  <p className="text-sm text-amber-800">
                    Du er i ferd med å godkjenne en studieplan som <strong>ikke oppfyller kvalitetskravene</strong> basert på KI-valideringen.
                    KI-scoren er <strong className="text-red-700">{Number(validationResult?.score || 0).toFixed(1)}%</strong> (under 70%).
                  </p>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <p className="font-medium text-gray-900 mb-2">Konsekvenser ved overprøving:</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir synlig for systemadministrator</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir synlig for eier-organisasjonen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Begrunnelsen blir <strong>ikke</strong> vist på offentlig verifiseringsside</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>Studieplanen vil likevel få godkjenningslenke og QR-kode</span>
                </li>
              </ul>
            </div>

            {/* KI Feedback summary */}
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="font-medium text-red-900 mb-1">KI-tilbakemelding:</p>
              <p className="text-sm text-red-800">{validationResult?.feedback?.summary || 'Ingen tilbakemelding'}</p>
            </div>

            {/* Override reason input */}
            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Begrunnelse for overprøving (påkrevd) *
              </label>
              <textarea
                className="input"
                rows={4}
                placeholder="Skriv en detaljert begrunnelse for hvorfor du godkjenner denne studieplanen til tross for lav KI-score..."
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 30 tegn. Begrunnelsen skal forklare hvilke kvalitetssikringsmekanismer som er brukt i stedet for KI-valideringen.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleOverrideApprove}
                disabled={approving || overrideReason.trim().length < 30}
                className="btn flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white disabled:bg-gray-400"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <AlertTriangle size={18} />
                )}
                Overprøv og godkjenn
              </button>
              <button
                onClick={() => {
                  setShowOverrideModal(false);
                  setOverrideReason('');
                }}
                disabled={approving}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit for Review Modal - for plans with rejected_with_revisions decision */}
      {showSubmitForReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="text-yellow-600" size={24} />
              Send inn med begrunnelse
            </h3>

            {/* Warning box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-yellow-900 mb-1">Studieplanen krever revisjon</p>
                  <p className="text-sm text-yellow-800">
                    KI-valideringen ga en score på <strong>{Number(validationResult?.score || 0).toFixed(1)}%</strong> (mellom 60-79%),
                    noe som betyr at planen trenger forbedringer før den kan selvgodkjennes.
                  </p>
                </div>
              </div>
            </div>

            {/* Info about submission */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <p className="text-sm text-blue-800">
                Du kan likevel sende studieplanen til studieforbundet for manuell vurdering.
                Du må oppgi en begrunnelse for hvorfor planen bør vurderes til tross for KI-tilbakemeldingen.
              </p>
            </div>

            {/* KI Feedback summary */}
            {validationResult?.feedback?.improvement_suggestions && validationResult.feedback.improvement_suggestions.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                <p className="font-medium text-gray-900 mb-2">KI-forslag til forbedring:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {validationResult.feedback.improvement_suggestions.slice(0, 3).map((suggestion: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Justification input */}
            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Begrunnelse for innsending (påkrevd) *
              </label>
              <textarea
                className="input"
                rows={4}
                placeholder="Forklar hvorfor studieplanen bør vurderes av studieforbundet til tross for at den ikke oppfyller alle KI-kravene..."
                value={submissionJustification}
                onChange={(e) => setSubmissionJustification(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 50 tegn. Begrunnelsen sendes til studieforbundet sammen med studieplanen.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {submissionJustification.length}/50 tegn
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmitForReview}
                disabled={submittingForReview || submissionJustification.trim().length < 50}
                className="btn flex-1 flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400"
              >
                {submittingForReview ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <AlertTriangle size={18} />
                )}
                Send til vurdering
              </button>
              <button
                onClick={() => {
                  setShowSubmitForReviewModal(false);
                  setSubmissionJustification('');
                }}
                disabled={submittingForReview}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
